/**
 * @alight/contracts
 * -----------------
 * Single source of truth shared by @alight/site and the Cloudflare Workers.
 * Exports: zod schemas, inferred TS types, and a typed fetch client
 * `apiClient(baseUrl)` covering every JSON route the API exposes.
 *
 * Keep this file framework-free (no React, no Hono, no Worker globals) so it
 * can be imported from the browser, the Worker runtime, and Node test runners.
 */
import { z } from "zod";

/* ===========================================================================
 * Enums / primitives
 * ========================================================================= */

export const PaymentStatus = z.enum([
  "unpaid",
  "pending_review",
  "partial",
  "paid",
  "refunded",
  "cancelled",
]);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const PaymentMethod = z.enum(["payid", "bank_transfer", "stripe", "cash", "already", "other"]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

/** The subset a parent can pick in the registration journey. */
export const JourneyPaymentMethod = z.enum(["payid", "cash", "already"]);
export type JourneyPaymentMethod = z.infer<typeof JourneyPaymentMethod>;

/**
 * `draft` is a registration the parent has NOT committed to.
 *
 * The journey has to create the row early — PayID cannot show bank details
 * without a reference to quote — but filling a form is not registering. Until
 * the family commits (PayID on confirming the transfer, card on settlement,
 * cash at submit) the row is a draft: it does not appear in My Registrations,
 * it is not in the host's lists, and completing any registration sweeps the
 * leftovers away.
 */
export const EnrolmentStatus = z.enum(["draft", "new", "confirmed", "withdrawn", "archived"]);
export type EnrolmentStatus = z.infer<typeof EnrolmentStatus>;

/**
 * WHO a registration is for.
 *
 * 'child' is every ordinary registration — a parent registering a child — and
 * is the default for every row that predates this. 'leader' is an adult or
 * junior leader registering THEMSELVES to help run a program: same row, same
 * payment reference, same reconciliation and reminders, but their own name
 * sits in `child_name` and no `children` record is created, so they never
 * appear on a roll or in the students list.
 */
export const RegistrantKind = z.enum(["child", "leader"]);
export type RegistrantKind = z.infer<typeof RegistrantKind>;

/** The leader form's under-18 question (migration 0036). */
export const JUNIOR_LEADER_FIELD = "junior_leader";

/**
 * IS THE LEADER BEING REGISTERED UNDER 18?
 *
 * Read from the answers themselves rather than from the date of birth: the
 * declared answer is the one the host rosters on, and a birthday typed in
 * wrongly must not quietly change who pays.
 *
 * Matched on the leading word ("Yes — under 18"), so the host can reword the
 * option in admin → Forms without the meaning moving, exactly as the form
 * itself matches it.
 *
 * Lives HERE because two places have to agree about it: the browser (which
 * previews the fee) and the Worker (which decides it). They disagreed once
 * over the family discount and a parent was shown one price and charged
 * another.
 */
export function declaresJuniorLeader(answers: Record<string, unknown> | null | undefined): boolean {
  const answer = answers?.[JUNIOR_LEADER_FIELD];
  return typeof answer === "string" && answer.trim().toLowerCase().startsWith("yes");
}

export const FormFieldType = z.enum([
  "text",
  "email",
  "tel",
  "textarea",
  "select",
  "checkbox",
  "number",
  "date",
]);
export type FormFieldType = z.infer<typeof FormFieldType>;

export const AssetKind = z.enum(["image", "video", "doc"]);
export type AssetKind = z.infer<typeof AssetKind>;

export const PaymentEventType = z.enum([
  "intent",
  "confirmed",
  "payment_recorded",
  "payment_voided",
  "payment_edited",
  /** What the registration is CHARGED was corrected — a waiver or discount
   *  that shouldn't have applied, a price fixed after the fact. See PATCH
   *  /api/admin/reconcile/enrolments/:ref/fee. */
  "fee_adjusted",
  "reminder_sent",
  "summary_sent",
  "reconcile_link_sent",
  /** The host asked this family, by hand, for money still owed — see
   *  POST /api/admin/reconcile/enrolments/:ref/request-payment. */
  "payment_request_sent",
  "refunded",
  "cancelled",
  /** A parent took a child off their own registration form — see
   *  POST /api/my/enrolments/:ref/withdraw. */
  "withdrawn",
]);
export type PaymentEventType = z.infer<typeof PaymentEventType>;

/** Payment reference format, e.g. ALC-7F3K or HOL-9Q2M. The prefix is the
 *  program's `ref_prefix` (2–6 A–Z/0–9, default "ALC"); the suffix is always
 *  four A–Z/0–9 characters. */
export const PaymentRef = z
  .string()
  .regex(/^[A-Z0-9]{2,6}-[A-Z0-9]{4}$/, "expected a payment reference like ALC-XXXX");
export type PaymentRef = z.infer<typeof PaymentRef>;

/* ===========================================================================
 * Enrolment
 * ========================================================================= */

export const Enrolment = z.object({
  id: z.number().int(),
  created_at: z.string(),
  parent_name: z.string().min(1),
  parent_email: z.string().email(),
  parent_phone: z.string().nullable().optional(),
  /** The child record this enrolment is assigned to (children table). */
  child_id: z.number().int().nullable().optional(),
  /** The registrant's name. For a leader registration this is the LEADER's own
   *  name — see `RegistrantKind`. */
  child_name: z.string().min(1),
  /** Who this registration is for. Defaults to 'child', so every row written
   *  before leaders existed reads exactly as it always did. */
  registrant_kind: RegistrantKind.default("child"),
  /** School year for term programs; "" for registrations whose form collects a
   *  date of birth instead (holiday programs — the dob lands in `extra`), and
   *  always "" for a leader. */
  year_level: z.string().default(""),
  program_id: z.string().nullable().optional(),
  amount_cents: z.number().int().default(5000),
  payment_ref: PaymentRef,
  payment_method: PaymentMethod.nullable().optional(),
  payment_status: PaymentStatus.default("unpaid"),
  intent_at: z.string().nullable().optional(),
  reminder_sent: z.number().int().default(0),
  paid_at: z.string().nullable().optional(),
  paid_by: z.string().nullable().optional(),
  status: EnrolmentStatus.default("new"),
  extra: z.record(z.unknown()).default({}),
});
export type Enrolment = z.infer<typeof Enrolment>;
/** Most children one family can register — and pay for — in one go. */
export const MAX_GROUPED_ENROLMENTS = 20;


/** Body for POST /api/enrol — server generates id/ref/timestamps.
 *  `parent_email` is OPTIONAL on the wire: when magic-link auth is configured
 *  the server takes the email from the verified session cookie and ignores the
 *  body value (the field remains for pre-auth/dev flows). The journey's
 *  payment commitment travels with the create: `payment_method` plus its
 *  follow-up (`cash_when` / `already_note`, stored in `extra`). */
export const EnrolmentInput = z.object({
  parent_name: z.string().min(1),
  parent_email: z.string().email().optional(),
  parent_phone: z.string().optional(),
  /** Existing child picked in the journey. The server honours it only while
   *  the record belongs to the session parent AND child_name still matches —
   *  otherwise it falls back to find-or-create by name. */
  child_id: z.number().int().positive().optional(),
  /** The registrant's name — the LEADER's own name when `registrant_kind` is
   *  'leader'. */
  child_name: z.string().min(1),
  /** Who is being registered. Omitted = 'child', so every existing client
   *  keeps working unchanged. A 'leader' registration writes no `children`
   *  record, is never priced by the family discount, and may be waived to $0
   *  by the program's `free_if_enrolled_program_id`. */
  registrant_kind: RegistrantKind.optional(),
  /**
   * Every child on the registration form, in the order shown — INCLUDING this
   * one, and including any already paid for.
   *
   * The form is what the parent reads, so the form decides the family: this
   * child's position in the list is the position they are priced at, and any
   * uncommitted registration for a child NOT in the list is withdrawn.
   *
   * It travels with each request so that deciding the price never depends on
   * database state a separate call might not have written yet — the earlier
   * design cleared a child with one request and re-priced with another, and
   * the re-price regularly won the race and charged a sibling's rate.
   *
   * Claiming a later position is not a way to a cheaper rate: the position is
   * only honoured as far as the earlier children in the list actually have
   * registrations, so an invented list prices HIGHER, never lower.
   */
  family: z.array(z.string().min(1)).max(MAX_GROUPED_ENROLMENTS).optional(),
  /** Optional: holiday-program registrations collect a date of birth (dynamic
   *  form → `extra.date_of_birth`) instead of a school year. */
  year_level: z.string().optional(),
  program_id: z.string().optional(),
  /**
   * The code from the host's private late-registration link (`?code=` on the
   * registration page — admin → Programs → "Late registration link").
   *
   * It waives THIS program's enrolment cut-off and nothing else: a finished,
   * hidden or info-only program still refuses, and a program that is open
   * needs no code, so sending a stale one never changes an ordinary signup.
   * Checked against the database on every submit (handlers/lateLinks.ts) — it
   * is a claim, not a permission.
   */
  late_code: z.string().max(100).optional(),
  payment_method: JourneyPaymentMethod.optional(),
  /** Concrete cash handover the parent committed to (method "cash"). */
  cash_when: z.string().max(200).optional(),
  /** How/when they paid (method "already") — the host matches it. */
  already_note: z.string().max(500).optional(),
  /**
   * ONE CONFIRMATION PER FAMILY, not per child.
   *
   * The journey creates a family one registration at a time (it has to — the
   * Nth child's price depends on the N-1 already written). Left alone that
   * sends a separate confirmation email per child, each quoting only that
   * child's share of a fee the family pays once: three emails saying $75, $65
   * and $60 for the $200 the screen just showed them, which reads as three
   * separate bills.
   *
   * So the creates say where they are in the run. `family_more` means another
   * child follows, and holds the parent's confirmation; `family_refs` are the
   * siblings already written, so the LAST create can send a single email
   * itemising every child and the family total.
   *
   * `family_refs` ALSO marks which sibling rows belong to the run in
   * progress, and that part is not presentational. Sibling position counts a
   * child ahead on the form only if their row was actually sent, or is named
   * here — otherwise a form filled in and abandoned leaves draft rows that
   * price the next child as a fourth or fifth, and on a table ending in 0
   * that is free. See handlers/enrol.ts. Omitting the refs is still safe: it
   * prices HIGHER (each child looks like a first child), never lower.
   *
   * `family_more` is purely presentational. Neither changes the host's
   * per-registration notification or the rows written, and the refs are only
   * honoured when they belong to the session parent.
   */
  family_refs: z.array(PaymentRef).max(MAX_GROUPED_ENROLMENTS).optional(),
  /** Another child in this submission follows this one — hold the parent's
   *  confirmation so the last create can send one covering them all. */
  family_more: z.boolean().optional(),
  extra: z.record(z.unknown()).optional(),
});
export type EnrolmentInput = z.infer<typeof EnrolmentInput>;

export const EnrolmentCreated = z.object({
  ref: PaymentRef,
  /** What the server actually charged, in cents — the authoritative figure once
   *  a family discount is in play (the fee for a second child is lower than the
   *  program's headline price). Optional so an older Worker that doesn't send it
   *  still parses; the client then falls back to its own preview. */
  amount_cents: z.number().int().nonnegative().optional(),
});
export type EnrolmentCreated = z.infer<typeof EnrolmentCreated>;

/** Body for PATCH /api/enrolments/:ref (session-owned updates). */
export const EnrolmentUpdate = z.object({
  parent_phone: z.string().optional(),
  payment_method: JourneyPaymentMethod.optional(),
  cash_when: z.string().max(200).optional(),
  already_note: z.string().max(500).optional(),
  extra: z.record(z.unknown()).optional(),
});
export type EnrolmentUpdate = z.infer<typeof EnrolmentUpdate>;

/* ===========================================================================
 * Auth (magic-link sign-in)
 * ========================================================================= */

export const AuthRequestLinkInput = z.object({
  email: z.string().email(),
  /** Same-origin path to land on after verification (server-sanitised). */
  redirect: z.string().optional(),
  /**
   * Which organisation the confirmation email should speak as — a co-brand key
   * (`programs.brand_key`, e.g. "htbh"). Sent by the branded registration pages
   * so a Holy Trinity family is never emailed by "Alight Creative", a name they
   * have not seen and would reasonably read as a scam. Unknown or absent means
   * the site's own name. Server-validated against the brand table; it only ever
   * picks wording, never permissions.
   */
  brand: z.string().optional(),
});
export type AuthRequestLinkInput = z.infer<typeof AuthRequestLinkInput>;

export const AuthRequestLinkResult = z.object({
  ok: z.literal(true),
  /** False when the email could not be delivered (link still valid). */
  emailed: z.boolean(),
  /** Local-dev/sandbox echo only (DEV_AUTH_ECHO=1) — never set in prod. */
  devLink: z.string().optional(),
});
export type AuthRequestLinkResult = z.infer<typeof AuthRequestLinkResult>;

export const AuthVerifyResult = z.object({
  email: z.string().email(),
  redirect: z.string(),
});
export type AuthVerifyResult = z.infer<typeof AuthVerifyResult>;

export const AuthMe = z.object({
  email: z.string().email().nullable(),
  /** True when the session email is on the ADMIN_EMAILS allow-list — lets the
   *  site nav surface the Admin link (the /admin area itself stays gated). */
  admin: z.boolean().default(false),
});
export type AuthMe = z.infer<typeof AuthMe>;

/* ===========================================================================
 * FormField (dynamic form builder)
 * ========================================================================= */

export const FormField = z.object({
  id: z.number().int(),
  form_key: z.string().min(1),
  label: z.string().min(1),
  name: z.string().min(1),
  type: FormFieldType,
  options: z.array(z.string()).default([]),
  /** Optional helper text rendered under the label on the public form. */
  description: z.string().default(""),
  required: z.number().int().min(0).max(1).default(0),
  sort_order: z.number().int().default(0),
  archived: z.number().int().min(0).max(1).default(0),
});
export type FormField = z.infer<typeof FormField>;

// Omitting `id` is enough: form_key/label/name/type stay required, while
// options/required/sort_order/archived already carry `.default()` so they are
// optional on input and fill their defaults when the host omits them. (Do NOT
// `.partial()` those — that wraps them in optional and bypasses the defaults.)
export const FormFieldInput = FormField.omit({ id: true });
export type FormFieldInput = z.infer<typeof FormFieldInput>;

/**
 * Questions that are the SAME question as a built-in one, asked under an older
 * name, mapped to the name the rest of the system knows them by.
 *
 * The holiday form asks the year level as "School Year" (Kindy–Year 6) and was
 * built as its own question, `school_year`. That put its answer in the
 * submission's free-form `extra` blob instead of the year level column the
 * roll, the check-in list, the Children screen and the submissions inbox all
 * read — so every holiday-club child showed no year at all on those screens.
 *
 * The registration journey renders these under their canonical name, so the
 * answer lands in the one year level everything reads. The stored form field
 * KEEPS its own name and its own wording: this is a reading of an existing
 * question, not a rewrite of it, so the host can still edit its label, its year
 * range and its ordering on the Forms screen exactly as before.
 */
const LEGACY_FIELD_NAMES: Record<string, string> = { school_year: "year_level" };

/** The name the system knows this question by — its own, unless it is one of
 *  the legacy names above. Safe on every other field: it returns them unchanged. */
export function canonicalFieldName(name: string): string {
  return LEGACY_FIELD_NAMES[name] ?? name;
}

/** The answer keys an older submission may have recorded a year level under,
 *  for reading back registrations taken before the mapping existed. */
export const LEGACY_YEAR_LEVEL_KEYS: readonly string[] = Object.keys(LEGACY_FIELD_NAMES).filter(
  (name) => LEGACY_FIELD_NAMES[name] === "year_level",
);

/* ===========================================================================
 * ContactMessage
 * ========================================================================= */

export const ContactMessage = z.object({
  id: z.number().int(),
  created_at: z.string(),
  name: z.string().min(1),
  email: z.string().email(),
  subject: z.string().min(1),
  message: z.string().min(1),
  status: z.string().default("new"),
  extra: z.record(z.unknown()).default({}),
});
export type ContactMessage = z.infer<typeof ContactMessage>;

export const ContactMessageInput = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  subject: z.string().min(1),
  message: z.string().min(1),
  extra: z.record(z.unknown()).optional(),
});
export type ContactMessageInput = z.infer<typeof ContactMessageInput>;

/* ===========================================================================
 * Asset
 * ========================================================================= */

export const Asset = z.object({
  id: z.number().int(),
  r2_key: z.string(),
  filename: z.string(),
  mime: z.string(),
  kind: AssetKind,
  category: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  alt: z.string().nullable().optional(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  bytes: z.number().int().nullable().optional(),
  used_on: z.array(z.string()).default([]),
  created_at: z.string(),
});
export type Asset = z.infer<typeof Asset>;

export const AssetCreated = z.object({ id: z.number().int(), url: z.string() });
export type AssetCreated = z.infer<typeof AssetCreated>;

/* ===========================================================================
 * PaymentEvent
 * ========================================================================= */

export const PaymentEvent = z.object({
  id: z.number().int(),
  enrolment_id: z.number().int(),
  type: PaymentEventType,
  actor: z.string().nullable().optional(),
  at: z.string(),
  detail: z.record(z.unknown()).default({}),
});
export type PaymentEvent = z.infer<typeof PaymentEvent>;

/* ===========================================================================
 * Partial payments (reconciliation ledger)
 * ========================================================================= */

/** One amount that actually landed against an enrolment (a ledger row). */
export const PaymentReceived = z.object({
  id: z.number().int(),
  enrolment_id: z.number().int(),
  amount_cents: z.number().int(),
  method: PaymentMethod.nullable().optional(),
  received_at: z.string().nullable().optional(),
  recorded_by: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  created_at: z.string(),
});
export type PaymentReceived = z.infer<typeof PaymentReceived>;

/** Body for POST /api/reconcile/enrolments/:ref/payments. */
export const PaymentRecordInput = z.object({
  amount_cents: z.number().int().positive(),
  method: PaymentMethod.optional(),
  /** When the money arrived, ISO date/datetime (defaults to now). */
  received_at: z.string().optional(),
  note: z.string().max(500).optional(),
  /**
   * Email the family about this payment? Defaults to TRUE — recording money
   * normally sends a receipt, or the "you're all set" confirmation when it
   * clears the balance, which is what every caller did before this existed.
   *
   * Set false when the money is being written down after the fact and the
   * family has already been told: catching up a backlog, entering cash taken
   * at the door, tidying a record. A receipt dated today for a transfer made
   * in March reads to a parent as a second charge.
   */
  notify: z.boolean().optional(),
});
export type PaymentRecordInput = z.infer<typeof PaymentRecordInput>;

/**
 * Body for PATCH /api/reconcile/enrolments/:ref/fee — correct the FEE on a
 * registration (what is OWED), as distinct from the payments against it.
 *
 * A fee can be wrong from the moment the row is written: a waiver or family
 * discount that should not have applied, a price changed after the family
 * registered, an amount typed by hand. Until this endpoint the only movable
 * money was the ledger, so a registration billed $0 by mistake was
 * permanently "Paid" and could never be chased.
 *
 * Zero is allowed — waiving a fee is as legitimate a correction as adding
 * one. The payment status is always recomputed from the corrected fee against
 * the money actually received, so a record moves in either direction.
 */
export const FeeAdjustInput = z.object({
  amount_cents: z.number().int().min(0),
  /** Why it changed. Kept on the record's history, not emailed. */
  reason: z.string().max(500).optional(),
});
export type FeeAdjustInput = z.infer<typeof FeeAdjustInput>;

/** Enrolment + live money position, as the reconcile screens see it. */
export const ReconcileEnrolment = Enrolment.extend({
  received_cents: z.number().int().default(0),
  balance_cents: z.number().int().default(0),
});
export type ReconcileEnrolment = z.infer<typeof ReconcileEnrolment>;

export const InvoiceStatus = z.enum(["issued", "paid", "void"]);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

/** The invoice this enrolment belongs to, if any — so a reconciler looking at
 *  ONE child can see that the bank line in front of them covers a family.
 *  Without this, a $200 transfer quoting a reference that owes $75 looks like
 *  an overpayment. */
export const ReconcileInvoice = z.object({
  id: z.number().int(),
  number: z.string(),
  total_cents: z.number().int(),
  status: InvoiceStatus,
  /** How many children this invoice covers, including the one being viewed. */
  children: z.number().int(),
  /** Every child on it, so the reconciler can split the payment without
   *  hunting for siblings. */
  lines: z.array(
    z.object({
      enrolment_id: z.number().int(),
      payment_ref: z.string().nullable().default(null),
      description: z.string(),
      amount_cents: z.number().int(),
      payment_status: z.string(),
    }),
  ).default([]),
});
export type ReconcileInvoice = z.infer<typeof ReconcileInvoice>;

export const ReconcileDetail = z.object({
  enrolment: ReconcileEnrolment,
  payments: z.array(PaymentReceived),
  events: z.array(PaymentEvent),
  /** Null for a single-child registration, or one made before invoicing. */
  invoice: ReconcileInvoice.nullable().default(null),
});
export type ReconcileDetail = z.infer<typeof ReconcileDetail>;

export const ReconcileSummaryResult = z.object({
  ok: z.literal(true),
  /** Number of payment events included in the summary email. */
  events: z.number().int(),
  emailed: z.boolean(),
});
export type ReconcileSummaryResult = z.infer<typeof ReconcileSummaryResult>;

/* ===========================================================================
 * Parent profile (saved details + previously enrolled children)
 * ========================================================================= */

/** The signed-in parent's saved details (account page; prefills /enrol). */
export const ParentProfile = z.object({
  name: z.string().nullable(),
  phone: z.string().nullable(),
  /** Null when derived from a past enrolment rather than a saved profile. */
  updated_at: z.string().nullable().optional(),
});
export type ParentProfile = z.infer<typeof ParentProfile>;

/** Body for PUT /api/my/profile — omitting `phone` clears it. */
export const ParentProfileInput = z.object({
  name: z.string().trim().min(1).max(200),
  phone: z.string().trim().max(50).optional(),
});
export type ParentProfileInput = z.infer<typeof ParentProfileInput>;

/** The photo/video permissions a parent can EXCLUDE on the enrolment form
 *  (form fields photo_exclude_activities / _social / _publications). Stored on
 *  the child as a comma-separated code list; `null`/"" = nothing excluded. */
export const PHOTO_EXCLUSION_LABELS: Record<string, string> = {
  activities: "ministry activities",
  social: "social media / website",
  publications: "church publications",
};

/** Which enrolment-form answer maps to which stored exclusion code. Lives here
 *  because THREE places must agree: the form the parent fills in, POST
 *  /api/enrol which records it on the child, and the admin edit dialog. */
export const PHOTO_EXCLUDE_FIELD_CODES: Record<string, string> = {
  photo_exclude_activities: "activities",
  photo_exclude_social: "social",
  photo_exclude_publications: "publications",
};

/** What gets stored on the child record for a set of exclusions. `photo_consent`
 *  stays the COARSE flag every existing screen and the CSV export already read:
 *  ticking anything (or naming another restriction) is a partial opt-out, which
 *  must not report as a free "yes". */
export function photoWishesFromCodes(
  codes: readonly string[],
  note?: string | null,
): { photo_consent: number; photo_exclusions: string | null; photo_exclude_note: string | null } {
  const known = Object.keys(PHOTO_EXCLUSION_LABELS).filter((code) => codes.includes(code));
  const trimmedNote = note && note.trim() !== "" ? note.trim() : null;
  return {
    photo_consent: known.length > 0 || trimmedNote !== null ? 0 : 1,
    photo_exclusions: known.length > 0 ? known.join(",") : null,
    photo_exclude_note: trimmedNote,
  };
}

/** Read the enrolment form's photo answers out of an enrolment's `extra`.
 *
 *  Returns null when the form DIDN'T ASK — the checkboxes are always submitted
 *  (true or false) whenever they're on the form, so their absence means this
 *  form has no photo section and the child's existing wishes must be left
 *  exactly as they are. Never infer consent from a question nobody saw. */
export function photoWishesFromAnswers(
  extra: Record<string, unknown>,
): ReturnType<typeof photoWishesFromCodes> | null {
  const asked = Object.keys(PHOTO_EXCLUDE_FIELD_CODES).some((key) => key in extra);
  if (!asked) return null;
  const codes = Object.entries(PHOTO_EXCLUDE_FIELD_CODES)
    .filter(([key]) => extra[key] === true)
    .map(([, code]) => code);
  const rawNote = extra.photo_exclude_other;
  return photoWishesFromCodes(codes, typeof rawNote === "string" ? rawNote : null);
}

/** Split the stored `photo_exclusions` code list into known codes, in a stable
 *  display order and ignoring anything unrecognised. */
export function parsePhotoExclusions(value: string | null | undefined): string[] {
  if (!value) return [];
  const found = new Set(
    value
      .split(",")
      .map((code) => code.trim().toLowerCase())
      .filter((code) => code !== ""),
  );
  return Object.keys(PHOTO_EXCLUSION_LABELS).filter((code) => found.has(code));
}

/** One-line summary of a child's photo wishes, for admin tables and the roll.
 *  Returns "" when the parent excluded nothing (nothing worth flagging). */
export function describePhotoExclusions(
  value: string | null | undefined,
  note?: string | null,
): string {
  const codes = parsePhotoExclusions(value);
  const parts = codes.map((code) => PHOTO_EXCLUSION_LABELS[code]);
  if (note && note.trim() !== "") parts.push(note.trim());
  if (parts.length === 0) return "";
  if (codes.length === Object.keys(PHOTO_EXCLUSION_LABELS).length && parts.length === codes.length) {
    return "No photos at all";
  }
  return `No photos: ${parts.join(", ")}`;
}

/** A child record (children table) — owned by parent_email, kept current
 *  with each enrolment; enrolments link back via enrolments.child_id. */
export const Child = z.object({
  id: z.number().int(),
  parent_email: z.string().email(),
  name: z.string().min(1),
  year_level: z.string().nullable().optional(),
  /** Free-text medical/allergy note; null = nothing recorded. */
  allergies: z.string().nullable().optional(),
  /** 1 = nothing excluded, 0 = something excluded, null = not answered. The
   *  COARSE flag — see photo_exclusions for what exactly the parent excluded. */
  photo_consent: z.number().int().min(0).max(1).nullable().optional(),
  /** Comma-separated codes the parent excluded (see PHOTO_EXCLUSION_LABELS). */
  photo_exclusions: z.string().nullable().optional(),
  /** The parent's free-text "other exclusions", if any. */
  photo_exclude_note: z.string().nullable().optional(),
  /** ISO timestamp when the child was archived; null = active. Archived children
   *  are hidden from the active Students list but keep their email + history. */
  archived_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Child = z.infer<typeof Child>;

/** Body for PATCH /api/admin/children/:id — edit a student record. Every field
 *  optional; a present null clears that field (allergies / photo_consent). */
export const ChildUpdateInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  year_level: z.string().trim().max(50).nullable().optional(),
  allergies: z.string().trim().max(1000).nullable().optional(),
  photo_consent: z.number().int().min(0).max(1).nullable().optional(),
  photo_exclusions: z.string().trim().max(200).nullable().optional(),
  photo_exclude_note: z.string().trim().max(500).nullable().optional(),
  /** The host's exclusion tags for this child (exclusion_tags ids) — "Term 4
   *  sport", "Away this term". Sent WHOLE: the list replaces whatever was
   *  there, so unticking a box is just a shorter array. Omit the field
   *  entirely to leave the child's tags alone. */
  exclusions: z.array(z.number().int().positive()).max(50).optional(),
});
export type ChildUpdateInput = z.infer<typeof ChildUpdateInput>;

/** Body for POST /api/admin/children/year-levels — the start-of-school-year
 *  review, where the host moves children up a year in one pass. Only the
 *  children whose year actually changes need to be sent; each one is recorded
 *  in the correction history like any other edit. */
export const YearLevelReviewInput = z.object({
  changes: z
    .array(
      z.object({
        id: z.number().int().positive(),
        year_level: z.string().trim().max(50).nullable(),
      }),
    )
    .min(1)
    .max(500),
});
export type YearLevelReviewInput = z.infer<typeof YearLevelReviewInput>;

/** Result of the start-of-year review — how many records actually moved. */
export const YearLevelReviewResult = z.object({
  updated: z.number().int().default(0),
});
export type YearLevelReviewResult = z.infer<typeof YearLevelReviewResult>;

/** Body for PATCH /api/admin/children/:id/archive — archive or restore a
 *  student record. archived=true hides the child from the active list (keeping
 *  their email + history); archived=false restores them. */
export const ChildArchiveInput = z.object({
  archived: z.boolean(),
});
export type ChildArchiveInput = z.infer<typeof ChildArchiveInput>;

/** Result of DELETE /api/admin/children/:id — a permanent removal that also
 *  cleans up the child's enrolment / attendance / payment rows. */
export const DeleteChildResult = z.object({
  ok: z.literal(true),
  deleted_enrolments: z.number().int(),
});
export type DeleteChildResult = z.infer<typeof DeleteChildResult>;

/** Result of DELETE /api/admin/enrolments/:id — a permanent removal of ONE
 *  registration (its attendance, payment ledger + events, invoice line, move
 *  requests and correction history go with it; the child record stays).
 *  `deleted_payments` counts the receipts that were erased, so the UI can say
 *  out loud when real money was attached to a row the host just removed. */
export const DeleteEnrolmentResult = z.object({
  ok: z.literal(true),
  deleted_payments: z.number().int(),
});
export type DeleteEnrolmentResult = z.infer<typeof DeleteEnrolmentResult>;

/**
 * Should the enrol journey make a returning parent RE-CONFIRM this child's year
 * level instead of quietly pre-filling last year's answer?
 *
 * The stored year level is whatever was last submitted, and the form pre-fills
 * it — which counts as "answered", so a returning parent clicks straight past
 * it and the child stays in the year they were in when they first registered.
 * That is the whole reason year levels drift out of date.
 *
 * The school year in Australia IS the calendar year, so the rule is simply: if
 * the child last enrolled in an earlier calendar year, their year level belongs
 * to a previous school year and has to be asked again. Enrolling twice in the
 * same year (Term 1 and Term 3) asks only once.
 *
 * A child with no stored year level needs no prompt — the field is required, so
 * the parent has to answer it anyway. A child with a year level but no
 * enrolment on record (added by the host, or imported) is treated as needing
 * confirmation: we have no idea how old that value is.
 */
export function yearLevelNeedsConfirming(
  child: { year_level?: string | null; last_enrolled_at?: string | null },
  now: Date = new Date(),
): boolean {
  if (!child.year_level || child.year_level.trim() === "") return false;
  if (!child.last_enrolled_at) return true;
  const last = new Date(child.last_enrolled_at);
  if (Number.isNaN(last.getTime())) return true;
  return last.getFullYear() < now.getFullYear();
}

/** A child of the signed-in parent, as the /enrol picker sees it. */
export const MyChild = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  year_level: z.string().nullable().optional(),
  last_enrolled_at: z.string().nullable().optional(),
});
export type MyChild = z.infer<typeof MyChild>;

/** GET /api/admin/children — child record + parent + enrolment aggregates. */
export const AdminChild = Child.extend({
  parent_name: z.string().nullable().optional(),
  parent_phone: z.string().nullable().optional(),
  enrolment_count: z.number().int().default(0),
  last_enrolled_at: z.string().nullable().optional(),
  /** Ids of the host's exclusion tags on this child (see ExclusionTag).
   *  Defaults to empty so a row read before the feature existed still parses. */
  exclusions: z.array(z.number().int()).default([]),
});
export type AdminChild = z.infer<typeof AdminChild>;

/** Body for POST /api/admin/enrolments — the host manually enrols a student
 *  into a program (a walk-up on the day, or an existing family whose parent
 *  hasn't re-submitted for the term). Distinct from the public EnrolmentInput:
 *  no auth session (the operator is trusted), the program is required, and the
 *  host sets the payment outcome directly instead of a parent-journey method.
 *
 *  `child_id` links to an existing student record (Students page); when it is
 *  omitted the server find-or-creates a child by parent_email + child_name,
 *  exactly like the public journey. `payment_status` is limited to the two
 *  outcomes the host actually chooses here — leave it `unpaid` (it then shows
 *  in Reconciliation to chase) or mark it `paid` (records a full payment on the
 *  ledger with `payment_method`, defaulting to cash). */
export const AdminEnrolmentInput = z.object({
  program_id: z.string().min(1),
  child_id: z.number().int().positive().optional(),
  parent_name: z.string().trim().min(1).max(200),
  parent_email: z.string().email(),
  parent_phone: z.string().trim().max(50).optional(),
  child_name: z.string().trim().min(1).max(200),
  year_level: z.string().trim().min(1).max(50),
  payment_status: z.enum(["unpaid", "paid"]).default("unpaid"),
  /** Recorded on the ledger when payment_status is "paid". Ignored otherwise. */
  payment_method: z.enum(["cash", "bank_transfer", "other"]).default("cash"),
  /** Email the parent their invoice — amount, reference, bank details and a
   *  Pay now link to /pay/:ref (which works signed out, so a walk-up with no
   *  account can still pay). Defaults OFF: this endpoint was silent by
   *  contract, and a bulk backfill must not mail every family retrospectively.
   *  The dialog opts IN for the case that motivated it — a new, unpaid
   *  registration taken at the door. */
  notify_parent: z.boolean().default(false),
  /** PART-DAY BOOKING: on a program that runs on named days (`Program.days`),
   *  the days this child is coming to. The server prices them with the same
   *  `day_pricing` table the public form uses, and the roll only lists the
   *  child on those days.
   *
   *  Omitted (every ordinary program, and a walk-up who is coming to the lot)
   *  means the whole program, exactly as before this existed. */
  days: z.array(z.string()).optional(),
  /** TICKET TYPE: on a program that sells differently-priced tickets
   *  (`Program.ticket_types`), the `key` of the ticket this child is on. The
   *  server prices from that ticket exactly as the public form does, and rejects
   *  a key the program does not offer. Omitted on every ordinary program. */
  ticket: z.string().optional(),
  /** THE SHIRT OPT-OUT: this family said they don't need a program shirt, so
   *  the program's opt-out amount comes off the fee and no shirt size is
   *  recorded. Ignored on a program that doesn't offer it (see
   *  `shirtOptOutCents`), so a stale tick can never invent a discount.
   *
   *  Omitted = they're having a shirt, which is every registration that
   *  predates this. */
  no_shirt: z.boolean().optional(),
});
export type AdminEnrolmentInput = z.infer<typeof AdminEnrolmentInput>;

/** Response for POST /api/admin/enrolments — the created enrolment, plus what
 *  became of the parent email.
 *
 *  Separate from `Enrolment` because sending is BEST-EFFORT: the registration
 *  is real whether or not the mail provider was reachable, so the request
 *  succeeds either way and reports honestly instead. `notified` false with a
 *  `notify_error` means "added, but they were not told" — which the host has
 *  to see, or they will assume a family was chased when it never was. */
export const AdminEnrolmentCreated = Enrolment.extend({
  notified: z.boolean().default(false),
  notify_error: z.string().nullable().default(null),
});
export type AdminEnrolmentCreated = z.infer<typeof AdminEnrolmentCreated>;

/** Body for PATCH /api/admin/enrolments/:id — the host CORRECTS what a family
 *  submitted (a mistyped year level, a wrong school, a changed emergency
 *  contact). Every field optional; only what is sent changes.
 *
 *  `extra` is a partial patch of the submitted form answers: a key set to null
 *  or "" clears that answer, and keys not mentioned are left alone — so the
 *  screen can send just the fields it shows without dropping the journey's
 *  internal keys (import provenance, payment notes).
 *
 *  Deliberately ABSENT: amount_cents, payment_ref, payment_status, paid_at and
 *  paid_by. Those are settled by the payment/reconciliation flows, and a
 *  free-text correction is the wrong instrument for them. program_id too —
 *  moving a child between programs changes the fee and the roster. */
export const EnrolmentUpdateInput = z.object({
  parent_name: z.string().trim().min(1).max(200).optional(),
  parent_phone: z.string().trim().max(50).nullable().optional(),
  child_name: z.string().trim().min(1).max(200).optional(),
  /** "" is valid — holiday registrations collect a date of birth instead. */
  year_level: z.string().trim().max(50).nullable().optional(),
  status: EnrolmentStatus.optional(),
  extra: z.record(z.unknown()).optional(),
});
export type EnrolmentUpdateInput = z.infer<typeof EnrolmentUpdateInput>;

/** Body for PATCH /api/admin/parents/:email — correct a family's contact
 *  details. `name` and `phone` also refresh the copies held on that family's
 *  enrolment snapshots, so the submissions list and the profile agree.
 *
 *  `email` is the consequential one: it is the family's sign-in identity and
 *  the key linking their children and enrolments, so changing it re-keys all of
 *  those records together and is refused if the new address already belongs to
 *  another family. The host portal warns before allowing it. */
export const ParentUpdateInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  phone: z.string().trim().max(50).nullable().optional(),
  email: z.string().email().optional(),
});
export type ParentUpdateInput = z.infer<typeof ParentUpdateInput>;

/** Result of PATCH /api/admin/parents/:email — the family's details after the
 *  correction, plus how many of their records the change reached. */
export const AdminParentUpdateResult = z.object({
  parent_email: z.string().email(),
  name: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  /** True when the sign-in email itself moved (children + enrolments re-keyed). */
  email_changed: z.boolean().default(false),
  children_updated: z.number().int().default(0),
  enrolments_updated: z.number().int().default(0),
});
export type AdminParentUpdateResult = z.infer<typeof AdminParentUpdateResult>;

/* ---------------------------------------------------------------------------
 * Moving ONE registration to another email address.
 *
 * ParentUpdateInput above changes a family's address by re-keying everything
 * they own. This is the narrow case it cannot serve: a registration typed
 * against the WRONG address, where the right address is already on file. Only
 * this child (their registrations, and an invoice that covers nobody else)
 * moves, and only once the DESTINATION address confirms by email — an admin
 * saying "this child is theirs" is not, on its own, evidence that they are.
 * See migrations/0039_enrolment_moves.sql.
 * ------------------------------------------------------------------------ */

/** Where a requested move has got to. `expired` is derived from the clock, not
 *  stored — an unclicked request just quietly stops being usable. */
export const EnrolmentMoveState = z.enum(["awaiting", "confirmed", "cancelled", "expired"]);
export type EnrolmentMoveState = z.infer<typeof EnrolmentMoveState>;

/** Body for POST /api/admin/enrolments/:id/move. */
export const EnrolmentMoveRequestInput = z.object({
  to_email: z.string().email(),
  /** The destination family's name, applied to the moved records on confirm. */
  to_name: z.string().trim().max(200).optional(),
  to_phone: z.string().trim().max(50).nullable().optional(),
  /** Tell the old address once the move completes. Default true. */
  notify_old: z.boolean().optional(),
});
export type EnrolmentMoveRequestInput = z.infer<typeof EnrolmentMoveRequestInput>;

/** One requested move, as the host portal lists it. */
export const EnrolmentMove = z.object({
  id: z.number().int(),
  enrolment_id: z.number().int(),
  child_id: z.number().int().nullable().optional(),
  from_email: z.string(),
  to_email: z.string(),
  to_name: z.string().nullable().optional(),
  to_phone: z.string().nullable().optional(),
  notify_old: z.boolean().default(true),
  requested_by: z.string().nullable().optional(),
  requested_at: z.string(),
  expires_at: z.string(),
  confirmed_at: z.string().nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  cancelled_by: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  state: EnrolmentMoveState,
});
export type EnrolmentMove = z.infer<typeof EnrolmentMove>;

/** One registration caught up in a move. */
export const EnrolmentMoveLine = z.object({
  id: z.number().int(),
  payment_ref: z.string(),
  child_name: z.string(),
  program_label: z.string().nullable().optional(),
});
export type EnrolmentMoveLine = z.infer<typeof EnrolmentMoveLine>;

/**
 * EXACTLY what a move would do, computed before anything is written — the
 * host approves a described change, not a verb. Re-read as the host types the
 * destination address, so "who is already there" is answered on screen instead
 * of by a 409 after the fact.
 */
export const EnrolmentMovePlan = z.object({
  enrolment_id: z.number().int(),
  child_name: z.string(),
  from_email: z.string(),
  to_email: z.string(),
  /** Every registration that moves, in date order. Always includes the one the
   *  host opened; longer when that child has enrolled more than once. */
  registrations: z.array(EnrolmentMoveLine).default([]),
  /** True when a `children` record moves too (false for a pre-0007 enrolment
   *  that was never linked to one). */
  moves_child: z.boolean().default(false),
  /** Invoice numbers that follow the child, and ones that cannot because they
   *  also bill another child who is staying put. */
  invoices_moving: z.array(z.string()).default([]),
  invoices_shared: z.array(z.string()).default([]),
  /** What the OLD address still holds afterwards. */
  remaining_children: z.number().int().default(0),
  remaining_enrolments: z.number().int().default(0),
  /** Whether the destination is already a family here, and who is under it. */
  destination_known: z.boolean().default(false),
  destination_children: z.array(z.string()).default([]),
  destination_name: z.string().nullable().optional(),
  /** Things the host should read before confirming, in plain language. */
  warnings: z.array(z.string()).default([]),
  /** Set when the move cannot be requested at all (e.g. same address). */
  blocked: z.string().nullable().optional(),
});
export type EnrolmentMovePlan = z.infer<typeof EnrolmentMovePlan>;

/** Result of requesting a move — nothing has moved yet. */
export const EnrolmentMoveRequested = z.object({
  move: EnrolmentMove,
  plan: EnrolmentMovePlan,
  /** False when the verification email could not be delivered. */
  emailed: z.boolean().default(false),
  /** Local/sandbox only (DEV_AUTH_ECHO=1): the confirmation link, so the
   *  journey is testable without an email provider. */
  devLink: z.string().optional(),
});
export type EnrolmentMoveRequested = z.infer<typeof EnrolmentMoveRequested>;

/** What the person holding the emailed link is being asked to confirm. Read
 *  without consuming the token, so the page can describe the move first. */
export const EnrolmentMoveInvite = z.object({
  state: z.union([EnrolmentMoveState, z.literal("unknown")]),
  child_name: z.string().nullable().optional(),
  program_label: z.string().nullable().optional(),
  to_email: z.string().nullable().optional(),
  org_name: z.string().nullable().optional(),
  registrations: z.number().int().default(0),
  expires_at: z.string().nullable().optional(),
});
export type EnrolmentMoveInvite = z.infer<typeof EnrolmentMoveInvite>;

/** Result of clicking the confirmation link. */
export const EnrolmentMoveConfirmed = z.object({
  ok: z.boolean().default(true),
  child_name: z.string().nullable().optional(),
  to_email: z.string().nullable().optional(),
  org_name: z.string().nullable().optional(),
  registrations_moved: z.number().int().default(0),
});
export type EnrolmentMoveConfirmed = z.infer<typeof EnrolmentMoveConfirmed>;

/** One recorded correction (migrations/0028_record_edits.sql). Host edits
 *  rewrite what a family actually submitted — including consents — so each
 *  changed field keeps its before/after and who changed it.
 *  `field` is a column name, or "extra.<key>" for a form answer. */
export const RecordEdit = z.object({
  id: z.number().int(),
  entity: z.enum(["enrolment", "child", "parent"]),
  /** enrolments.id / children.id as text, or the parent's email. */
  entity_id: z.string(),
  field: z.string(),
  old_value: z.string().nullable().optional(),
  new_value: z.string().nullable().optional(),
  actor: z.string().nullable().optional(),
  at: z.string(),
});
export type RecordEdit = z.infer<typeof RecordEdit>;

/** GET /api/admin/db-dump — every user table's RAW rows, keyed by table name.
 *  No per-table schema on purpose: the dump is lossless (TEXT JSON columns
 *  stay strings) and table-agnostic, so new migrations need no contract edit. */
export const AdminDbDump = z.object({
  tables: z.record(z.array(z.record(z.unknown()))),
});
export type AdminDbDump = z.infer<typeof AdminDbDump>;

/** GET /api/my/profile — saved details plus the returning-family child list. */
export const MyProfile = z.object({
  profile: ParentProfile.nullable(),
  children: z.array(MyChild).default([]),
});
export type MyProfile = z.infer<typeof MyProfile>;

/* ===========================================================================
 * Bulk import (POST /api/admin/import — silent, idempotent record inserts)
 *
 * Rows link by NATURAL KEYS, never database ids: a child row carries its
 * parent's email, an enrolment row carries parent_email + child_name. The
 * worker resolves/creates the linkage (parent profile upsert, child
 * find-or-create) internally, so replaying the identical body against a
 * different database (draft sandbox → live on publish) is id-safe.
 * ========================================================================= */

/** One parent-profile row to upsert (keyed by email — the account key). */
export const ImportParentProfileRow = z.object({
  parent_email: z.string().email(),
  name: z.string().min(1),
  phone: z.string().max(50).optional(),
});
export type ImportParentProfileRow = z.infer<typeof ImportParentProfileRow>;

/** One child row — find-or-create by (parent_email, case-insensitive name). */
export const ImportChildRow = z.object({
  parent_email: z.string().email(),
  name: z.string().min(1),
  year_level: z.string().max(50).optional(),
  /** Optional medical/allergy note and photo permission (1/0), if the sheet
   *  carries them — applied to the child record on find-or-create. */
  allergies: z.string().max(1000).optional(),
  photo_consent: z.number().int().min(0).max(1).optional(),
});
export type ImportChildRow = z.infer<typeof ImportChildRow>;

/** One enrolment row — the child is resolved (or created) by
 *  parent_email + child_name; payment_ref is generated server-side. */
export const ImportEnrolmentRow = z.object({
  parent_email: z.string().email(),
  child_name: z.string().min(1),
  /** Parent display name for the enrolment row (falls back to the imported /
   *  saved parent profile, then to the email's mailbox part). */
  parent_name: z.string().optional(),
  parent_phone: z.string().max(50).optional(),
  year_level: z.string().max(50).optional(),
  /** A program that already exists on the LIVE site (numeric id as string).
   *  Programs created in the same draft may get a different id on publish. */
  program_id: z.string().optional(),
  /** Fee override in cents; defaults to the program's price (or the standard
   *  default) like a normal enrolment. */
  amount_cents: z.number().int().min(0).optional(),
  payment_status: PaymentStatus.optional(),
  status: EnrolmentStatus.optional(),
});
export type ImportEnrolmentRow = z.infer<typeof ImportEnrolmentRow>;

/** Body for POST /api/admin/import. `import_id` is the idempotency key: the
 *  same id can be POSTed any number of times, only the first applies. */
export const ImportRequest = z
  .object({
    import_id: z.string().min(8).max(64),
    parent_profiles: z.array(ImportParentProfileRow).max(2000).optional(),
    children: z.array(ImportChildRow).max(2000).optional(),
    enrolments: z.array(ImportEnrolmentRow).max(2000).optional(),
  })
  .refine(
    (v) =>
      (v.parent_profiles?.length ?? 0) +
        (v.children?.length ?? 0) +
        (v.enrolments?.length ?? 0) >
      0,
    { message: "import carries no rows" },
  );
export type ImportRequest = z.infer<typeof ImportRequest>;

/** Result of an import — replayed verbatim (with already_applied=true) when
 *  the same import_id is POSTed again. */
export const ImportResult = z.object({
  import_id: z.string(),
  already_applied: z.boolean(),
  counts: z.object({
    parent_profiles: z.number().int().default(0),
    children: z.number().int().default(0),
    enrolments: z.number().int().default(0),
  }),
  created: z.object({
    children: z.array(z.number().int()).default([]),
    enrolments: z.array(z.number().int()).default([]),
  }),
});
export type ImportResult = z.infer<typeof ImportResult>;

/* ===========================================================================
 * Enrolment draft (resumable journey)
 * ========================================================================= */

export const EnrolmentDraft = z.object({
  /** JSON snapshot of the journey state (values, program, method, step). */
  data: z.record(z.unknown()),
  updated_at: z.string(),
});
export type EnrolmentDraft = z.infer<typeof EnrolmentDraft>;

/* ===========================================================================
 * Payment intent / confirm
 * ========================================================================= */

export const PaymentIntentInput = z.object({
  ref: PaymentRef,
  /**
   * Every registration the ONE transfer covers, when a family registered
   * several children together. `ref` stays the reference the parent was shown
   * and actually typed into their banking app.
   *
   * Declaring the whole group in one call is what makes the follow-up sane:
   * one "we've got your transfer" email quoting the family total instead of
   * one per child, one reconcile task for the account manager instead of one
   * per child, and one invoice. Omitted = the single-registration behaviour,
   * unchanged. Only references the session parent owns are accepted.
   */
  refs: z.array(PaymentRef).min(1).max(MAX_GROUPED_ENROLMENTS).optional(),
});
export type PaymentIntentInput = z.infer<typeof PaymentIntentInput>;

export const PaymentIntentResult = z.object({
  ref: PaymentRef,
  payment_status: PaymentStatus,
  intent_at: z.string(),
  clear_days: z.number().int(),
});
export type PaymentIntentResult = z.infer<typeof PaymentIntentResult>;

export const PaymentConfirmResult = z.object({
  id: z.number().int(),
  payment_status: PaymentStatus,
  paid_at: z.string(),
  paid_by: z.string(),
});
export type PaymentConfirmResult = z.infer<typeof PaymentConfirmResult>;

/** POST /api/payments/checkout — start a Stripe-hosted card payment for a
 *  reference. The amount is ALWAYS computed server-side from the enrolment's
 *  outstanding balance; the client never proposes a price. */
/** Most children one grouped payment may cover. Stripe caps a metadata VALUE
 *  at 500 characters and the refs ride in one — ~55 would fit, so this is a
 *  sanity bound on a family, not a technical limit. */

export const CheckoutSessionInput = z.object({
  ref: PaymentRef,
  /**
   * Every registration this ONE card payment covers, when a family registered
   * several children together. `ref` stays the one the parent is shown.
   *
   * Named explicitly rather than inferred from "everything this parent owes":
   * a family can have an unpaid registration on a DIFFERENT program, and a
   * card form that quietly swept that in would charge for something the parent
   * is not looking at. The server still decides the amounts — it only accepts
   * references the session parent owns, and prices each from the database.
   */
  refs: z.array(PaymentRef).min(1).max(MAX_GROUPED_ENROLMENTS).optional(),
});
export type CheckoutSessionInput = z.infer<typeof CheckoutSessionInput>;

export const CheckoutSessionResult = z.object({
  ref: PaymentRef,
  /** Hand to Stripe.js to mount the card form inside our own page. Null only
   *  if the server was asked for a redirect session instead. */
  client_secret: z.string().nullable().default(null),
  /** Stripe-hosted page to redirect to — the fallback path, null for an
   *  embedded session. */
  url: z.string().url().nullable().default(null),
  /** What the session will charge, for the "you'll be charged X" confirmation. */
  amount_cents: z.number().int(),
});
export type CheckoutSessionResult = z.infer<typeof CheckoutSessionResult>;

/** "Tim", "Tim and Ava", "Tim, Ava and Sam" — a readable list, never a bare
 *  comma run. Shared by the grouped-payment email and the pay page so the
 *  parent sees their children named the same way in both. */
export function formatNameList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}


/** POST /api/payments/checkout/group — one card payment covering EVERY
 *  outstanding enrolment belonging to the signed-in parent.
 *
 *  Deliberately takes no refs: the server derives them from the session, so a
 *  caller can't group references that aren't theirs, and knowing one reference
 *  never reveals how many children a family has. Sign-in required for the same
 *  reason — grouping is the one payment path that exposes family shape. */
export const GroupCheckoutResult = z.object({
  client_secret: z.string().nullable().default(null),
  url: z.string().url().nullable().default(null),
  /** Total to be charged — the sum of the outstanding balances. */
  amount_cents: z.number().int(),
  /** The references this payment settles, for the confirmation copy. */
  refs: z.array(PaymentRef).min(1),
});
export type GroupCheckoutResult = z.infer<typeof GroupCheckoutResult>;

/** GET /api/payments/group-summary — what a grouped payment WOULD cover, so
 *  the pay/my screens can offer it only when it's actually worth offering. */
export const GroupSummary = z.object({
  /** Outstanding enrolments for the signed-in parent (0 when nothing owing). */
  count: z.number().int().nonnegative(),
  total_cents: z.number().int().nonnegative(),
  refs: z.array(PaymentRef).default([]),
  /** Child names, purely for the "pay for Tim, Ava and Sam" confirmation. */
  child_names: z.array(z.string()).default([]),
});
export type GroupSummary = z.infer<typeof GroupSummary>;

/* ===========================================================================
 * Invoices — the family-level record of what was billed
 * ========================================================================= */


export const InvoiceLine = z.object({
  id: z.number().int(),
  enrolment_id: z.number().int(),
  /** Frozen at issue — e.g. "Holiday Club — Tim (2nd child)". */
  description: z.string(),
  amount_cents: z.number().int(),
});
export type InvoiceLine = z.infer<typeof InvoiceLine>;

export const Invoice = z.object({
  id: z.number().int(),
  /** Human reference, e.g. INV-2026-0041. */
  number: z.string(),
  parent_email: z.string(),
  parent_name: z.string().nullable().default(null),
  total_cents: z.number().int(),
  status: InvoiceStatus,
  issued_at: z.string(),
  paid_at: z.string().nullable().default(null),
  /** The reference a parent quotes for one transfer covering the whole family. */
  payment_ref: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  lines: z.array(InvoiceLine).default([]),
});
export type Invoice = z.infer<typeof Invoice>;

/** POST /api/invoices — issue one invoice over the caller's OWN enrolments.
 *  The server re-checks ownership of every reference, so a caller can't bill
 *  someone else's child or pull another family's amounts into their record. */
/** Answer to emailing an invoice out. */
export const InvoiceSendResult = z.object({
  ok: z.boolean(),
  number: z.string(),
  sent_to: z.string(),
});
export type InvoiceSendResult = z.infer<typeof InvoiceSendResult>;

export const InvoiceCreateInput = z.object({
  refs: z.array(PaymentRef).min(1).max(MAX_GROUPED_ENROLMENTS),
});
export type InvoiceCreateInput = z.infer<typeof InvoiceCreateInput>;

/** POST /api/admin/reconcile/enrolments/:ref/request-payment — ask ONE family
 *  for money still owed, in the host's own words. The amount, reference, bank
 *  details and recipient all come from the record on the server, so the only
 *  thing the caller supplies is the message. */
export const PaymentRequestInput = z.object({
  message: z.string().trim().min(1).max(2000),
});
export type PaymentRequestInput = z.infer<typeof PaymentRequestInput>;

/** Answer to sending a payment request. */
export const PaymentRequestResult = z.object({
  ok: z.boolean(),
  sent_to: z.string(),
  balance_cents: z.number().int(),
});
export type PaymentRequestResult = z.infer<typeof PaymentRequestResult>;

/** POST /api/admin/reconcile/enrolments/:ref/request-family-payment — the same
 *  ask, widened to every registration under that parent's address.
 *
 *  `refs` are the registrations actually CHARGED for. `shown` counts the lines
 *  ON the email, which is larger whenever settled siblings were displayed for
 *  reassurance — they are never part of the total. */
export const FamilyPaymentRequestResult = z.object({
  ok: z.boolean(),
  sent_to: z.string(),
  total_outstanding_cents: z.number().int(),
  /** The single reference the family was asked to quote on one transfer. */
  quoted_ref: z.string(),
  refs: z.array(z.string()),
  shown: z.number().int(),
});
export type FamilyPaymentRequestResult = z.infer<typeof FamilyPaymentRequestResult>;

/** One row of the outbound email log (GET /api/admin/emails).
 *  `kind` is free text rather than an enum on purpose — a new sort of message
 *  must never need a contracts release before it can be recorded. */
export const EmailLogEntry = z.object({
  id: z.number().int(),
  to_email: z.string(),
  subject: z.string(),
  body: z.string(),
  kind: z.string(),
  payment_ref: z.string().nullable(),
  status: z.string(),
  provider_id: z.string().nullable(),
  error: z.string().nullable(),
  sent_at: z.string(),
});
export type EmailLogEntry = z.infer<typeof EmailLogEntry>;

/** Public, minimal view of one enrolment's payment position
 *  (GET /api/payments/:ref/summary). Deliberately excludes names/contact
 *  details — the ref alone must not unlock personal data. */
export const PaymentSummary = z.object({
  ref: PaymentRef,
  payment_status: PaymentStatus,
  /** The full fee frozen on the enrolment. */
  amount_cents: z.number().int(),
  /** What is still owed (fee minus recorded payments, floored at 0). */
  balance_cents: z.number().int(),
  /** How the parent said they'd pay (payid/cash/already), null if unchosen. */
  payment_method: z.string().nullable(),
  program_title: z.string().nullable(),
});
export type PaymentSummary = z.infer<typeof PaymentSummary>;

/* ===========================================================================
 * Program (static fallback now, API-driven later)
 * ========================================================================= */

/**
 * One ticket option on a program that sells differently-priced tickets (see
 * `Program.ticket_types`). The registrant picks exactly one.
 *
 * Money is integer CENTS. `early_bird_cents` is the price charged on or before
 * the program's `early_bird_until` date (null = this ticket has no early-bird
 * price, so it always costs `amount_cents`). `capacity` caps how many of THIS
 * ticket can be sold (null = unlimited) — the Lodge sleeps 26, the campsite is
 * open, so Lodging carries a cap and Tenting does not.
 */
export const TicketType = z.object({
  /** Stable machine key stored against each registration, e.g. "tenting". Never
   *  shown to a parent and never re-used for a different ticket, so a price or
   *  label can change without rewriting what past registrations chose. */
  key: z.string().min(1),
  /** What the parent sees, e.g. "Tenting" or "Lodging (cabin bed)". */
  label: z.string().min(1),
  /** Standard price in cents (after early bird, or when there is no early bird). */
  amount_cents: z.number().int().nonnegative(),
  /** Optional early-bird price in cents, charged on/before `early_bird_until`.
   *  Null = no early-bird price for this ticket. */
  early_bird_cents: z.number().int().nonnegative().nullable().default(null),
  /** Optional cap on how many of this ticket may be sold. Null = unlimited. */
  capacity: z.number().int().nonnegative().nullable().default(null),
  /** Optional one-line note shown under the ticket, e.g. "Bring your own tent". */
  note: z.string().default(""),
  /** Optional RESERVE LINK: the `key` of another ticket that must SELL OUT
   *  before this one becomes available. Used for overflow beds — a "Reserve"
   *  ticket revealed automatically only once the main lodging ticket is full,
   *  charged at a lower rate. Null (the default) = always available. See
   *  `hidden`, computed server-side from this. */
  reveal_when_sold_out: z.string().nullable().default(null),
  /** READ-ONLY, server-computed on the public programs feed: this ticket has
   *  reached its capacity and can no longer be chosen. Never stored; always
   *  false on admin/write paths. */
  sold_out: z.boolean().default(false),
  /** READ-ONLY, server-computed on the public programs feed: this ticket is a
   *  reserve (`reveal_when_sold_out` is set) whose linked ticket has NOT sold
   *  out yet, so it is not offered. Never stored; always false on admin/write
   *  paths. */
  hidden: z.boolean().default(false),
});
export type TicketType = z.infer<typeof TicketType>;

export const Program = z.object({
  id: z.number().int(),
  title: z.string().min(1),
  term: z.string().default(""),
  schedule: z.string().default(""),
  location: z.string().default(""),
  price_note: z.string().default(""),
  amount_cents: z.number().int().default(5000),
  launch_date: z.string().default(""),
  active: z.number().int().min(0).max(1).default(1),
  /** 1 = open for enrolment (card shows an "Enrol Now" button); 0 = an
   *  informational listing only (no button, no enrol flow). Defaults to 1 so
   *  every existing program stays enrollable. */
  enrollable: z.number().int().min(0).max(1).default(1),
  sort_order: z.number().int().default(0),
  /** Structured schedule for weekly attendance (the free-text `schedule` /
   *  `launch_date` above stay for display). `lesson_weekday` is 0=Sun … 5=Fri
   *  … 6=Sat; `first_lesson_date` is the ISO date (YYYY-MM-DD) of week 1 — the
   *  anchor the attendance screen generates weekly lessons from. Both nullable:
   *  a program without them simply has no auto-generated lessons yet. */
  lesson_weekday: z.number().int().min(0).max(6).nullable().default(null),
  first_lesson_date: z.string().nullable().default(null),
  /** Optional multi-day EVENT span — for special programs / holiday clubs that
   *  run a few days in a row rather than a weekly class. Both ISO dates
   *  (YYYY-MM-DD), nullable: a normal weekly class leaves them null and shows no
   *  date row. When set, the public card renders a formatted range via
   *  `formatEventDates` (e.g. "Wed 30 Sep – Fri 2 Oct 2026"). `end_date` may be
   *  omitted for a single-day event. */
  start_date: z.string().nullable().default(null),
  end_date: z.string().nullable().default(null),
  /** Optional preview image shown at the top of the public Programs card — a
   *  full public URL (e.g. an image from the Assets library). Null = no image,
   *  and the card renders exactly as it did before this field existed. */
  image_url: z.string().nullable().default(null),
  /** Optional last day to enrol (ISO YYYY-MM-DD), INCLUSIVE — enrolment stays
   *  open all day on this date and closes the day after, judged in Sydney time.
   *  Null = no cut-off (the default), which is how every program behaved before
   *  this field existed. Drives the "Enrol Now" button and is enforced again on
   *  the server when a registration is submitted — see `isEnrolmentClosed`. */
  enrol_cutoff_date: z.string().nullable().default(null),
  /** Who the program is FOR, in the host's own words — "Years 3 and up",
   *  "K-6", "Ages 5-12". Free text on purpose: year levels, school stages and
   *  ages are all used depending on the program. Null/"" = not stated, and the
   *  public card renders no "Who" row (how every program behaved before this
   *  field existed). */
  age_range: z.string().nullable().default(null),
  /** Which registration form the enrol journey renders for this program:
   *  'enrolment' (the term form, with the school-year question) or 'holiday'
   *  (date of birth + safety/medical questions). Defaults to 'enrolment' so
   *  every existing program is unaffected. */
  form_key: z.string().min(1).default("enrolment"),
  /** Payment-reference prefix for this program's registrations (e.g. "HOL" →
   *  HOL-XXXX). 2–6 uppercase letters/digits; defaults to "ALC". */
  ref_prefix: z
    .string()
    .regex(/^[A-Z0-9]{2,6}$/, "prefix must be 2-6 uppercase letters/digits")
    .default("ALC"),
  /** Optional rich notes shown on the public program card. Limited HTML,
   *  sanitised server-side (see workers/api sanitizeHtml). "" = no notes. */
  notes_html: z.string().default(""),
  /** Optional SIBLING DISCOUNT: cumulative family TOTALS in cents, indexed by
   *  (children - 1), the last entry acting as the cap. E.g.
   *  [7500, 14000, 20000, 25000] = $75 for one child, $140 for two, $200 for
   *  three, $250 for four or more. The first entry is the single-child price
   *  and should match `amount_cents`.
   *
   *  Null (the default, and every program that predates this) means no family
   *  discount — each child is charged `amount_cents`, exactly as before. See
   *  `feeForNextChildCents`, which turns the table into the amount charged for
   *  one more child. */
  family_pricing: z.array(z.number().int().nonnegative()).nullable().default(null),
  /** Optional PART-DAY BOOKING: the individual days this program runs, e.g.
   *  ["Wednesday 30 September", "Thursday 1 October", "Friday 2 October"].
   *
   *  Null or empty — the default, and every program that predates this — means
   *  the program is booked as a WHOLE: no day question is asked and the price
   *  is `amount_cents` as it always was. With days listed, the journey asks
   *  each child which of them they are coming to, and `day_pricing` decides
   *  what that costs. */
  days: z.array(z.string()).nullable().default(null),
  /** Optional PER-DAY PRICING: what ONE child pays for 1, 2, 3 … days, in
   *  cents, indexed by (days chosen - 1). E.g. [3500, 5500, 7500] = $35 for
   *  one day, $55 for two, $75 for three or more.
   *
   *  Null means the day choice does not change the price — every combination
   *  costs `amount_cents`. See `feeForDaysCents`. */
  day_pricing: z.array(z.number().int().nonnegative()).nullable().default(null),
  /** Optional TICKET TYPES: a program (a camp, an event) where each registrant
   *  picks ONE of several named options, each with its own price — "Tenting"
   *  vs "Lodging", say. Distinct from `days` (pick SEVERAL, cumulative) and
   *  `family_pricing` (per-child sibling discount): a ticket is an exclusive
   *  single choice that SETS the price for that child.
   *
   *  Null or empty — the default, and every program that predates this — means
   *  no ticket question: the price is `amount_cents` / the family or day tables
   *  exactly as before. With tickets listed, the journey asks each child which
   *  ticket, and the chosen ticket decides what they pay. See `ticketPriceCents`
   *  and `findTicket`. A program with tickets does not also use the day or
   *  family tables for its price — the ticket is the price. */
  ticket_types: z.array(TicketType).nullable().default(null),
  /** Optional EARLY-BIRD cut-off (ISO YYYY-MM-DD), INCLUSIVE and judged in
   *  Sydney time, exactly like `enrol_cutoff_date`. On or before this date,
   *  a ticket's `early_bird_cents` is charged (where set); after it, the ticket's
   *  standard `amount_cents`. Null = no early-bird window, so every ticket always
   *  charges its standard price. Only meaningful alongside `ticket_types`. See
   *  `isEarlyBird`. */
  early_bird_until: z.string().nullable().default(null),
  /** Optional CO-BRANDING key: which partner's branded registration page this
   *  program uses (see apps/site/src/lib/brand.ts for the brands themselves).
   *  Null — the default — means the ordinary Alight Creative journey at /enrol.
   *
   *  The site matches programs to brands on THIS key and never on an id: an id
   *  means something different in every database, which is how the branded page
   *  once ended up pointed at the wrong program entirely. */
  brand_key: z.string().nullable().default(null),
  /** Shown on the public Programs page and offered in the enrol journey's
   *  program chooser? 0 = LINK-ONLY: the program is still live and a
   *  `?program=` link still resolves, it simply isn't advertised. That is the
   *  difference between this and `active = 0`, which hides the program from
   *  the public API altogether and so breaks the link too.
   *
   *  Defaults to 1, so every program that predates this stays listed. */
  listed: z.number().int().min(0).max(1).default(1),
  /** Optional FEE WAIVER: this program is free for anyone who already holds a
   *  registration on the program with this id. Used by the leaders sign-up —
   *  a parent whose child is registered volunteers at no charge, proven by
   *  their own registration under the same verified email rather than by a
   *  claim anyone has to check. Null (the default) = everyone pays. */
  free_if_enrolled_program_id: z.number().int().nullable().default(null),
  /** ISO timestamp when the host ARCHIVED this program; null = live (the
   *  default, so every program that predates the column is unchanged).
   *
   *  Archiving says "this is over and filed away": the program drops off the
   *  public programs page, its registrations leave the Submissions inbox, and
   *  its families become the "past, not currently enrolled" mailing audience.
   *  Nothing is deleted and Restore undoes all of it.
   *
   *  Deliberately a host action rather than a date rule — see
   *  `looksReadyToArchive`, which only SUGGESTS. */
  archived_at: z.string().nullable().default(null),
  created_at: z.string().nullable().optional(),
  extra: z.record(z.unknown()).default({}),
});
export type Program = z.infer<typeof Program>;

/**
 * The total a family pays for `childCount` children on this program.
 *
 * With no pricing table it is simply the flat fee per child. With one, it is
 * the tier for that many children, and any count beyond the table's length pays
 * the last tier (the cap) — so "4 or more" needs no special case.
 */
export function familyTotalCents(
  rates: readonly number[] | null | undefined,
  childCount: number,
  perChildCents: number,
): number {
  if (childCount <= 0) return 0;
  let total = 0;
  for (let i = 0; i < childCount; i++) total += rateForChild(rates, i, perChildCents);
  return total;
}

/**
 * What ONE MORE child costs a family that already has `existingChildren` on
 * this program — the difference between the family total before and after.
 *
 * This is what makes a per-child registration journey add up to a per-family
 * price: enrolling three children one at a time charges $75, then $65, then
 * $60, totalling exactly the $200 on the table. Never negative, so a table that
 * is accidentally non-increasing charges $0 rather than a refund.
 */
export function feeForNextChildCents(
  rates: readonly number[] | null | undefined,
  existingChildren: number,
  perChildCents: number,
): number {
  return rateForChild(rates, Math.max(0, existingChildren), perChildCents);
}

/**
 * What the (index+1)-th child costs, where index is 0-based.
 *
 * THE RULE: a rate that isn't defined copies the last one that is. A table of
 * [7500, 6500, 6000] charges $75, $65, then $60 for the third child AND every
 * child after — you never have to enumerate a rate for a family of nine.
 *
 * A genuine cap is opt-in: append a 0 and children beyond that point are free.
 * That is the whole reason these are per-child rates rather than cumulative
 * family totals. Under totals, the table's LENGTH silently decided when
 * children became free, so a three-row table quietly gave the fourth child
 * away — the opposite of what a program that costs money per child wants.
 */
function rateForChild(
  rates: readonly number[] | null | undefined,
  index: number,
  perChildCents: number,
): number {
  if (!rates || rates.length === 0) return perChildCents;
  const rate = rates[Math.min(index, rates.length - 1)];
  return typeof rate === "number" && rate >= 0 ? rate : perChildCents;
}

/** One row of a program's published family discount, ready to show a parent. */
export interface FamilyPricingRow {
  /** Family size this row prices. */
  children: number;
  /** True on the last row — it also covers any LARGER family ("3 or more"). */
  isCap: boolean;
  /** What the whole family pays at this size, in cents. */
  totalCents: number;
  /** What this row's child adds to the family total — what they're charged
   *  when they register, in cents. */
  nextChildCents: number;
  /** Saved against paying the full per-child fee that many times, in cents. */
  savingCents: number;
}

/**
 * A program's family-discount table turned into ROWS A PARENT CAN READ, so the
 * discount is visible on the program card and in the journey rather than only
 * showing up as a smaller number at payment time.
 *
 * Returns [] when the program has no discount (nothing to show) — including a
 * one-entry table, which is just the flat fee.
 *
 * Trailing tiers that repeat the one before are folded into a single capped
 * row: [75, 140, 200, 200] is "1 child · 2 children · 3 or more", not a fourth
 * row charging a fourth child nothing.
 */
export function familyPricingRows(
  rates: readonly number[] | null | undefined,
  perChildCents: number,
): FamilyPricingRow[] {
  if (!rates || rates.length < 2) return [];
  // Trailing repeats say nothing new — the last rate already covers every
  // later child — so fold them into one "and each child after" row.
  const shown = [...rates];
  while (shown.length > 1 && shown[shown.length - 1] === shown[shown.length - 2]) shown.pop();
  if (shown.length < 2) return [];
  return shown.map((_, i) => {
    const children = i + 1;
    const totalCents = familyTotalCents(rates, children, perChildCents);
    return {
      children,
      isCap: children === shown.length,
      totalCents,
      nextChildCents: rateForChild(rates, i, perChildCents),
      savingCents: Math.max(0, perChildCents * children - totalCents),
    };
  });
}

/** Does this program let a family pick which days they are coming? */
export function hasDayChoice(program: {
  days?: readonly string[] | null;
}): boolean {
  return (program.days?.length ?? 0) > 0;
}

/**
 * What ONE child pays for `dayCount` of a program's days.
 *
 * Same rule as the family discount: a count that isn't priced copies the last
 * one that is, so [3500, 5500, 7500] charges $75 for three days AND for any
 * longer program without enumerating every count.
 *
 * With no day pricing, every combination costs the program's ordinary fee —
 * which is what "days are for attendance, not for price" looks like.
 *
 * NOTHING CHOSEN COSTS THE FULL FEE, not nothing. A day count of zero means a
 * question that should have been answered wasn't, and the safe direction for a
 * price nobody has established is the one the program already advertises. The
 * journey requires the answer and the server rejects an empty choice, so this
 * is the third line of defence rather than a real path — but it must not be
 * the line that hands out free places.
 */
export function feeForDaysCents(
  dayRates: readonly number[] | null | undefined,
  dayCount: number,
  perChildCents: number,
): number {
  if (!dayRates || dayRates.length === 0) return perChildCents;
  if (dayCount <= 0) return perChildCents;
  return rateForChild(dayRates, dayCount - 1, perChildCents);
}

/* ---------------------------------------------------------------------------
 * THE SHIRT OPT-OUT — "they don't need a shirt, take it off the price".
 *
 * A holiday club's fee includes a program shirt, which is fine when a child
 * comes for the whole week. A child coming for ONE day of three is being
 * charged for a shirt they may not want, and the church would rather not order
 * one that nobody wears — so a family booking selected days can decline it and
 * pay less.
 *
 * The rule lives here, next to the day pricing, because the journey (preview),
 * the Worker (the charge) and the walk-up dialog at the door all have to agree
 * about it. Anywhere it is decided twice, it is decided differently.
 * ------------------------------------------------------------------------ */

/** The registration question the opt-out answers instead of — hidden when the
 *  family declines the shirt, because a size for a shirt nobody is getting is
 *  a question with no right answer (and an order line waiting to happen). */
export const SHIRT_SIZE_FIELD = "shirt_size";

/** Where the answer is filed in a registration's `extra`. `true` = no shirt. */
export const NO_SHIRT_ANSWER = "no_shirt";

/** Taken off the fee when the shirt is declined, unless the program says
 *  otherwise — see `shirtOptOutCents`. */
export const DEFAULT_SHIRT_OPTOUT_CENTS = 1000;

/**
 * What THIS program takes off for going without a shirt, in cents.
 * 0 = the opt-out is not offered and no shirt question is ever hidden.
 *
 * Offered on a program that is booked BY DAYS, which is the case it exists
 * for: a part-day place. The amount is the host's, editable per program in
 * admin -> Programs (stored on the program's `extra`), and setting it to 0
 * turns the offer off for that program. A program booked as a whole doesn't
 * offer it unless the host puts an amount on it deliberately.
 *
 * Nothing here decides whether the QUESTION exists — the shirt-size question
 * comes from the registration form, so a form that never asks about shirts
 * shows no opt-out however this is set.
 */
export function shirtOptOutCents(program: {
  days?: readonly string[] | null;
  extra?: Record<string, unknown> | null;
}): number {
  const raw = program.extra?.shirt_optout_cents;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) return Math.round(raw);
  return hasDayChoice(program) ? DEFAULT_SHIRT_OPTOUT_CENTS : 0;
}

/**
 * A child's fee once the shirt opt-out is applied. Never below zero: a
 * discount larger than the fee makes a place free, never a refund.
 */
export function feeAfterShirtOptOut(
  feeCents: number,
  optOutCents: number,
  declined: boolean,
): number {
  if (!declined || optOutCents <= 0) return feeCents;
  return Math.max(0, feeCents - optOutCents);
}

/**
 * Which of a program's days a session is.
 *
 * Sessions of a multi-day event are numbered from its start date (see
 * `sessionIndexFor` in the attendance handler), so session 1 is the first
 * label, session 2 the second, and so on. Null when the program isn't
 * part-day bookable, or when there are more sessions than labelled days —
 * an unlabelled day is nobody's "not booked" day.
 */
export function sessionDayLabel(
  programDays: readonly string[] | null | undefined,
  sessionIndex: number,
): string | null {
  if (!programDays || programDays.length === 0) return null;
  return programDays[sessionIndex - 1] ?? null;
}

/**
 * Is this registration on the roll for this session?
 *
 * Used by the roll, the check-in kiosk, the grid and the attendance rate, so
 * that a family who booked Wednesday and Friday is simply not on Thursday's
 * sheet — and Thursday is not counted against their attendance.
 *
 * EVERY uncertain case answers YES (on the roll), because the cost of hiding
 * a child who is standing in the room is far worse than the cost of an extra
 * name: a program with no day choice, a registration that recorded no days (a
 * manual/admin enrolment, or one taken before the question existed), and a
 * session past the end of the labelled days all put the child on the roll,
 * exactly as before part-day booking existed.
 */
export function isBookedForSession(
  programDays: readonly string[] | null | undefined,
  sessionIndex: number,
  bookedDays: readonly string[] | null | undefined,
): boolean {
  const label = sessionDayLabel(programDays, sessionIndex);
  if (!label) return true;
  if (!bookedDays || bookedDays.length === 0) return true;
  return bookedDays.includes(label);
}

/**
 * Format a program's optional event span (start_date/end_date, ISO YYYY-MM-DD)
 * into a human range for the card, e.g. "Wed 30 Sep – Fri 2 Oct 2026". Returns
 * "" when there is no start date (a normal weekly class). A single-day event
 * (no/equal end date) renders just the one day. Framework-free and UTC-based so
 * the date never shifts by timezone.
 */
export function formatEventDates(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MON = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const parse = (d: string | null | undefined) => {
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d.trim())) return null;
    const dt = new Date(`${d.trim()}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    return {
      dow: DOW[dt.getUTCDay()],
      day: dt.getUTCDate(),
      mon: MON[dt.getUTCMonth()],
      year: dt.getUTCFullYear(),
    };
  };
  const s = parse(start);
  if (!s) return "";
  const e = parse(end);
  const head = `${s.dow} ${s.day} ${s.mon}`;
  // Single day (no end, or an end equal to / before the start).
  if (!e || (e.day === s.day && e.mon === s.mon && e.year === s.year)) {
    return `${head} ${s.year}`;
  }
  // Show the start year only when it differs from the end year.
  const startPart = s.year !== e.year ? `${head} ${s.year}` : head;
  return `${startPart} – ${e.dow} ${e.day} ${e.mon} ${e.year}`;
}

/**
 * Today's date as ISO YYYY-MM-DD in Sydney — the only clock that matters for
 * "has enrolment closed?". The Worker runs in UTC and a parent's browser runs
 * in whatever timezone their device says, so both must agree with the host's
 * local calendar or a cut-off would land up to a day early/late.
 */
export function todayInSydney(now?: Date): string {
  // en-CA formats as YYYY-MM-DD, which is exactly the shape we store.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now ?? new Date());
}

/**
 * Has this program's enrolment cut-off passed? INCLUSIVE of the cut-off day —
 * a cut-off of 2026-09-25 keeps enrolment open all of the 25th and closes on
 * the 26th. No cut-off (null/blank/malformed) is never closed, so programs
 * without a date behave exactly as they always have.
 *
 * Shared by the public card, the enrol journey and the Worker's submit
 * endpoint so the button and the server can never disagree.
 */
export function isEnrolmentClosed(
  program: { enrol_cutoff_date?: string | null },
  today: string = todayInSydney(),
): boolean {
  const cutoff = (program.enrol_cutoff_date ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(cutoff)) return false;
  return today > cutoff;
}

/**
 * Is this program's EARLY-BIRD window still open? INCLUSIVE of the cut-off day,
 * judged in Sydney time — identical rule to `isEnrolmentClosed`, so the card,
 * the journey and the server all agree on which price is live. No window
 * (null/blank/malformed `early_bird_until`) is NOT early bird, so a program with
 * no window always charges standard — which is what a ticket with no early-bird
 * price wants anyway.
 *
 * Shared by the public card, the enrol journey and the Worker's submit endpoint
 * so the quoted price and the charged price can never disagree.
 */
export function isEarlyBird(
  program: { early_bird_until?: string | null },
  today: string = todayInSydney(),
): boolean {
  const until = (program.early_bird_until ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(until)) return false;
  return today <= until;
}

/** Does this program sell differently-priced tickets a registrant chooses between? */
export function hasTicketChoice(program: {
  ticket_types?: readonly TicketType[] | null;
}): boolean {
  return (program.ticket_types?.length ?? 0) > 0;
}

/** The ticket with this key, or null — matched case-insensitively and trimmed so
 *  a stale or hand-edited key still resolves to the ticket the host defined. */
export function findTicket(
  program: { ticket_types?: readonly TicketType[] | null },
  key: string | null | undefined,
): TicketType | null {
  const want = (key ?? "").trim().toLowerCase();
  if (!want) return null;
  return program.ticket_types?.find((t) => t.key.trim().toLowerCase() === want) ?? null;
}

/**
 * What ONE registrant pays for a ticket, in cents. Early bird (`earlyBird`
 * true) uses `early_bird_cents` when the ticket sets one; otherwise, and always
 * after the window, the ticket's standard `amount_cents`.
 *
 * Decided from the TICKET, never trusted from the browser — the journey shows
 * this number as a live quote, and the server charges it from the same helper,
 * so the two cannot disagree.
 */
export function ticketPriceCents(ticket: TicketType, earlyBird: boolean): number {
  if (earlyBird && ticket.early_bird_cents != null) return ticket.early_bird_cents;
  return ticket.amount_cents;
}

/**
 * Is this reserve ticket still hidden? A ticket gated with
 * `reveal_when_sold_out` is offered only once the ticket it names has reached
 * capacity — the overflow beds that open automatically when the main lodging is
 * full. `isSoldOut(key)` answers whether a given ticket key is at capacity.
 *
 * An ungated ticket (no link) is never hidden. A link naming a ticket that
 * cannot be found stays hidden, which is the safe direction: better an overflow
 * bed that never appears than cheaper beds offered while the full-price ones
 * still have space.
 */
export function ticketHidden(
  ticket: { reveal_when_sold_out?: string | null },
  isSoldOut: (key: string) => boolean,
): boolean {
  const gate = (ticket.reveal_when_sold_out ?? "").trim();
  if (!gate) return false;
  return !isSoldOut(gate);
}

/**
 * Has this program FINISHED? Inclusive of the last day — an end date of
 * 2026-06-26 keeps the program listed all of the 26th and retires it on the
 * 27th. A program with no end date (null/blank/malformed) NEVER finishes, so
 * every program that predates this behaves exactly as it always has and the
 * host stays in control by simply leaving the field empty.
 *
 * This is what stops a finished term sitting on the programs page for months
 * with a live "Enrol Now" button — previously the only way to retire a term
 * was to remember to untick "Active" by hand.
 */
export function hasProgramFinished(
  program: { end_date?: string | null },
  today: string = todayInSydney(),
): boolean {
  const end = (program.end_date ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return false;
  return today > end;
}

/**
 * Should this program be shown on the public programs page? Visible, and not
 * past its end date. Kept separate from `canEnrolNow` because a program can
 * legitimately be listed but closed to enrolment (an info-only card, or one
 * past its enrolment cut-off but still running).
 */
export function isProgramListed(
  program: { active: number; listed?: number; end_date?: string | null; archived_at?: string | null },
  today: string = todayInSydney(),
): boolean {
  /* `listed` is optional here and compared against 0 rather than 1: a program
     read from a database that predates the column has no value at all, and
     the safe reading of "we don't know" is the one every program had before —
     listed. Only an explicit 0 hides it. */
  return (
    program.active === 1 &&
    program.listed !== 0 &&
    !isProgramArchived(program) &&
    !hasProgramFinished(program, today)
  );
}

/**
 * Has the host archived this program? Filed away as over — see
 * `Program.archived_at`.
 *
 * Optional and null-tolerant because the same predicate is handed program rows
 * from a database that predates the column, where "no value" means "live".
 */
export function isProgramArchived(program: { archived_at?: string | null }): boolean {
  return typeof program.archived_at === "string" && program.archived_at.trim() !== "";
}

/**
 * Does this program LOOK like it is over, and so worth offering to archive?
 *
 * A suggestion, never an action. Two signals, either one enough:
 *   - its end date has passed (a holiday club that ran in September), or
 *   - the host has already switched it off — inactive, or unlisted and closed
 *     to enrolment (a term class that simply stopped, with no end date at all).
 *
 * That second branch is the one that matters. The programs the host actually
 * wanted out of the inbox included a Term 3 class with no end date recorded, so
 * a purely date-based nudge would have stayed silent on exactly the row they
 * were pointing at.
 *
 * Already-archived programs are never "ready" — there is nothing left to offer.
 */
export function looksReadyToArchive(
  program: {
    active: number;
    listed?: number;
    enrollable?: number;
    end_date?: string | null;
    enrol_cutoff_date?: string | null;
    archived_at?: string | null;
  },
  today: string = todayInSydney(),
): boolean {
  if (isProgramArchived(program)) return false;
  if (hasProgramFinished(program, today)) return true;
  if (program.active !== 1) return true;
  return program.listed === 0 && isEnrolmentClosed(program, today);
}

/**
 * Can a parent enrol in this program right now? The single gate behind the
 * "Enrol Now" button: the program must be visible, not finished, marked open
 * for enrolment, and still inside its cut-off.
 */
export function canEnrolNow(
  program: {
    active: number;
    enrollable: number;
    end_date?: string | null;
    enrol_cutoff_date?: string | null;
    archived_at?: string | null;
  },
  today: string = todayInSydney(),
): boolean {
  return (
    program.active === 1 &&
    program.enrollable === 1 &&
    !isProgramArchived(program) &&
    !hasProgramFinished(program, today) &&
    !isEnrolmentClosed(program, today)
  );
}

/** Body for POST /api/admin/programs — server owns id/created_at.
 *
 *  `archived_at` is omitted too: archiving is its own deliberate action with
 *  its own endpoint, so an ordinary "save the program" from the edit dialog can
 *  never silently un-archive something by round-tripping a stale field. */
export const ProgramInput = Program.omit({
  id: true,
  created_at: true,
  archived_at: true,
});

/** Body for PATCH /api/admin/programs/:id/archive — file a finished program
 *  away (archived=true) or put it back (archived=false). */
export const ProgramArchiveInput = z.object({
  archived: z.boolean(),
});
export type ProgramArchiveInput = z.infer<typeof ProgramArchiveInput>;
export type ProgramInput = z.infer<typeof ProgramInput>;

/**
 * A LATE REGISTRATION INVITE — the private link the host hands to ONE family
 * who asks after registrations have closed.
 *
 * One row per family, not per program: revoking the link you sent the Smiths
 * leaves the Joneses' working. The `code` is what goes in the registration
 * page's `?code=`, and it waives that program's enrolment cut-off and nothing
 * else — only for `email`, and only until `expires_at`. Admin-only: no public
 * endpoint ever returns one.
 */
export const ProgramLateInvite = z.object({
  id: z.number().int(),
  program_id: z.number().int(),
  code: z.string(),
  /** The ONE address this link works for, checked against the verified
   *  magic-link session on submit. Forwarding the link achieves nothing. */
  email: z.string(),
  /** The host's own note about who it is for. */
  note: z.string().nullable().default(null),
  created_at: z.string(),
  /** Which admin made it — blank on a site without Access configured. */
  created_by: z.string().nullable().default(null),
  /** 48 hours after it was made. Past this it is refused like a wrong code. */
  expires_at: z.string(),
  /** When the host turned this one off, or null while it is live. */
  revoked_at: z.string().nullable().default(null),
  /** A family registers each child separately, so this counts registrations,
   *  not families — 3 means one household with three children. */
  used_count: z.number().int().default(0),
  last_used_at: z.string().nullable().default(null),
});
export type ProgramLateInvite = z.infer<typeof ProgramLateInvite>;

/** Why a `?code=` did or didn't open a closed program — see the Worker's
 *  handlers/lateLinks.ts. `sign_in` is a yes that is waiting for the family to
 *  prove who they are. */
export const LateLinkVerdict = z.enum([
  "ok",
  "sign_in",
  "wrong_email",
  "expired",
  "revoked",
  "unknown",
]);
export type LateLinkVerdict = z.infer<typeof LateLinkVerdict>;

/** GET /api/late-link/check. `ok` decides whether the journey puts a form up;
 *  `reason` and the masked `email_hint` decide what it says while it does. */
export const LateLinkCheck = z.object({
  ok: z.boolean(),
  reason: LateLinkVerdict.optional(),
  /** `k•••••••@gmail.com`, and only for a code genuinely issued for this
   *  program — never the address in full, to anyone. */
  email_hint: z.string().nullish(),
  expires_at: z.string().nullish(),
});
export type LateLinkCheck = z.infer<typeof LateLinkCheck>;

/* ===========================================================================
 * Attendance — weekly lessons + per-student marks
 *
 * A `ProgramSession` is one lesson (one week) of a program. `attendance` marks
 * one enrolled student's presence at one lesson. Everything here is Access-
 * gated admin data (the /admin/attendance screen and its reporting).
 * ========================================================================= */

/** How a student was for a given lesson. `null` (absent from the payload) means
 *  "not yet marked" — the sheet leaves the row blank until an admin picks one. */
export const AttendanceStatus = z.enum(["here", "late", "away", "sick", "holiday"]);
export type AttendanceStatus = z.infer<typeof AttendanceStatus>;

export const SessionStatus = z.enum(["scheduled", "cancelled"]);
export type SessionStatus = z.infer<typeof SessionStatus>;

/** One lesson (one week) of a program. */
export const ProgramSession = z.object({
  id: z.number().int(),
  program_id: z.number().int(),
  /** ISO date (YYYY-MM-DD) of the lesson. */
  session_date: z.string(),
  week_number: z.number().int(),
  title: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  status: SessionStatus.default("scheduled"),
  created_at: z.string().nullable().optional(),
  extra: z.record(z.unknown()).default({}),
});
export type ProgramSession = z.infer<typeof ProgramSession>;

/** A lesson plus how many of its roster have been marked (list/reporting view). */
export const AttendanceSessionSummary = ProgramSession.extend({
  /** Enrolments on the roster for this lesson (not cancelled/withdrawn). */
  roster_count: z.number().int().default(0),
  /** How many of the roster have a mark recorded. */
  marked_count: z.number().int().default(0),
  /** Count of each status recorded for the lesson. */
  counts: z.record(z.number().int()).default({}),
});
export type AttendanceSessionSummary = z.infer<typeof AttendanceSessionSummary>;

/** Body for POST /api/admin/attendance/sessions — add/prepare a lesson.
 *  Omit `session_date` to append the next weekly lesson after the latest one. */
export const AddSessionInput = z.object({
  program_id: z.number().int(),
  session_date: z.string().optional(),
});
export type AddSessionInput = z.infer<typeof AddSessionInput>;

/** Body for PATCH /api/admin/attendance/sessions/:id — lesson metadata. */
export const SessionUpdateInput = z.object({
  title: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  status: SessionStatus.optional(),
});
export type SessionUpdateInput = z.infer<typeof SessionUpdateInput>;

/** One roster row on the marking sheet: the enrolled student plus their mark
 *  for the selected lesson (`status` null until marked). */
export const AttendanceMark = z.object({
  enrolment_id: z.number().int(),
  child_id: z.number().int().nullable().optional(),
  child_name: z.string(),
  year_level: z.string().nullable().optional(),
  parent_name: z.string().nullable().optional(),
  /** Student's medical/allergy note (from the child record) — shown on the roll. */
  allergies: z.string().nullable().optional(),
  /** 1 = photo OK, 0 = opted out, null = unknown. */
  photo_consent: z.number().int().min(0).max(1).nullable().optional(),
  /** Exactly WHAT the parent excluded — shown on the roll so a helper with a
   *  camera knows "not on Instagram" from "no photos at all". */
  photo_exclusions: z.string().nullable().optional(),
  photo_exclude_note: z.string().nullable().optional(),
  /** PART-DAY BOOKING: the program days this registration actually booked, on
   *  a program that lets families pick days (`Program.days`). Null/absent for
   *  every ordinary registration — booked for the whole program. The roll only
   *  lists a part-day child on the days they booked, and flags them here so a
   *  leader can see who is not here all week. */
  days: z.array(z.string()).nullable().optional(),
  payment_status: PaymentStatus,
  status: AttendanceStatus.nullable().optional(),
  note: z.string().nullable().optional(),
  marked_at: z.string().nullable().optional(),
  marked_by: z.string().nullable().optional(),
});
export type AttendanceMark = z.infer<typeof AttendanceMark>;

/** GET /api/admin/attendance/sheet?session_id= — a lesson + its roster/marks. */
export const AttendanceSheet = z.object({
  session: ProgramSession,
  program: Program,
  rows: z.array(AttendanceMark).default([]),
});
export type AttendanceSheet = z.infer<typeof AttendanceSheet>;

/** Body for POST /api/admin/attendance/mark — record (or clear) one student's
 *  mark for one lesson. `status: null` clears the mark (back to unmarked). */
export const MarkAttendanceInput = z.object({
  session_id: z.number().int(),
  enrolment_id: z.number().int(),
  status: AttendanceStatus.nullable(),
  note: z.string().max(500).nullable().optional(),
});
export type MarkAttendanceInput = z.infer<typeof MarkAttendanceInput>;

/** One student's aggregated attendance across a whole program (reporting). */
export const AttendanceStudentReport = z.object({
  enrolment_id: z.number().int(),
  child_id: z.number().int().nullable().optional(),
  child_name: z.string(),
  year_level: z.string().nullable().optional(),
  counts: z.record(z.number().int()).default({}),
  /** Lessons marked for this student (any status). */
  marked: z.number().int().default(0),
  /** Total scheduled lessons in the program to date. */
  total_sessions: z.number().int().default(0),
  /** Lessons attended (here + late) as a share of total lessons, 0–100. */
  attendance_rate: z.number().default(0),
});
export type AttendanceStudentReport = z.infer<typeof AttendanceStudentReport>;

/** GET /api/admin/attendance/summary?program_id= — the per-program report. */
export const AttendanceSummary = z.object({
  program: Program,
  sessions: z.array(AttendanceSessionSummary).default([]),
  students: z.array(AttendanceStudentReport).default([]),
});
export type AttendanceSummary = z.infer<typeof AttendanceSummary>;

/** One student's row in the whole-term grid (spreadsheet view). */
export const AttendanceGridStudent = z.object({
  enrolment_id: z.number().int(),
  child_id: z.number().int().nullable().optional(),
  child_name: z.string(),
  year_level: z.string().nullable().optional(),
  /** session_id (as string) → status, for the weeks this student was marked. */
  marks: z.record(AttendanceStatus).default({}),
  /** PART-DAY BOOKING: the program days this registration booked (see
   *  `AttendanceMark.days`). The grid greys the days they did not book instead
   *  of showing them as an unmarked lesson. */
  days: z.array(z.string()).nullable().optional(),
});
export type AttendanceGridStudent = z.infer<typeof AttendanceGridStudent>;

/** GET /api/admin/attendance/grid?program_id= — the full term as a matrix:
 *  lessons (chronological) × students, every recorded mark in one payload. */
export const AttendanceGrid = z.object({
  program: Program,
  sessions: z.array(ProgramSession).default([]),
  students: z.array(AttendanceGridStudent).default([]),
});
export type AttendanceGrid = z.infer<typeof AttendanceGrid>;

/* ===========================================================================
 * Settings (host-configurable copy / bank details / timing)
 * ========================================================================= */

export const SettingGroup = z.enum(["bank", "notices", "timing"]);
export type SettingGroup = z.infer<typeof SettingGroup>;

/** `order` renders a reorderable, show/hide list instead of a text box — see
 *  parseMethodConfig. The stored value is still a plain string, so nothing in
 *  the settings pipeline needs to know about it. */
export const SettingKind = z.enum(["text", "multiline", "number", "order"]);
export type SettingKind = z.infer<typeof SettingKind>;

/** One catalogue entry as the admin portal sees it: the EFFECTIVE value
 *  (saved row → env var → built-in default) plus whether a row exists. */
export const AdminSetting = z.object({
  key: z.string().min(1),
  label: z.string(),
  description: z.string(),
  group: SettingGroup,
  kind: SettingKind,
  value: z.string(),
  /** True when the host saved an override (a `settings` row exists). */
  custom: z.boolean(),
});
export type AdminSetting = z.infer<typeof AdminSetting>;

/** Body for PUT /api/admin/settings — null/empty value reverts to default. */
export const AdminSettingSaveInput = z.object({
  key: z.string().min(1),
  value: z.string().nullable(),
});
export type AdminSettingSaveInput = z.infer<typeof AdminSettingSaveInput>;

/** Public payment details (GET /api/payment-config) — what the enrol/pay
 *  pages render so they always match the payment-instruction emails. */
/** Every payment option the pay page can offer, in the DEFAULT order. The
 *  host reorders (and hides) them via the `payments.method_order` setting;
 *  this array is the fallback and the validation whitelist. */
export const PayMethod = z.enum(["card", "payid", "cash", "already"]);
export type PayMethod = z.infer<typeof PayMethod>;
export const PAY_METHOD_DEFAULT_ORDER: PayMethod[] = ["card", "payid", "cash", "already"];

/** One payment option as the host has configured it. */
export interface MethodConfigRow {
  method: PayMethod;
  /** False when the host has switched this option off for parents. */
  visible: boolean;
}

/**
 * Parse the stored `payments.method_order` value into EVERY known method, in
 * the host's display order, each flagged visible or not.
 *
 * Format is a comma-separated list where a leading `-` means hidden:
 *   `card,payid,-cash,already`  → cash exists but parents can't pick it.
 *
 * Hiding is explicit rather than "leave it out", because anything unlisted is
 * APPENDED as visible — that way adding a payment method in code can never
 * make it silently invisible on a site whose setting predates it. Those two
 * rules would contradict each other if omission also meant hidden.
 *
 * Unknown entries and duplicates are dropped (a duplicate would render the
 * same option twice, with clashing React keys).
 */
export function parseMethodConfig(value: string | null | undefined): MethodConfigRow[] {
  const seen = new Set<PayMethod>();
  const rows: MethodConfigRow[] = [];
  for (const raw of (value ?? "").split(",")) {
    const trimmed = raw.trim().toLowerCase();
    const hidden = trimmed.startsWith("-");
    const parsed = PayMethod.safeParse(hidden ? trimmed.slice(1) : trimmed);
    if (parsed.success && !seen.has(parsed.data)) {
      seen.add(parsed.data);
      rows.push({ method: parsed.data, visible: !hidden });
    }
  }
  for (const m of PAY_METHOD_DEFAULT_ORDER) {
    if (!seen.has(m)) rows.push({ method: m, visible: true });
  }
  return rows;
}

/** Serialise the editor's rows back to the stored string. */
export function formatMethodConfig(rows: readonly MethodConfigRow[]): string {
  return rows.map((r) => (r.visible ? r.method : `-${r.method}`)).join(",");
}

/** The options a parent can actually pick, in order. `card` is additionally
 *  dropped when Stripe isn't configured — see PaymentConfig. */
export function parseMethodOrder(value: string | null | undefined): PayMethod[] {
  return parseMethodConfig(value)
    .filter((r) => r.visible)
    .map((r) => r.method);
}

/** Parent-facing labels, shared by the pay page and the admin reorder list so
 *  the host reorders the words parents actually read. */
export const PAY_METHOD_LABELS: Record<PayMethod, string> = {
  card: "Card",
  payid: "Bank transfer",
  cash: "Cash",
  already: "I've already paid",
};

export const PaymentConfig = z.object({
  bank_account_name: z.string(),
  bank_bsb: z.string(),
  bank_account_number: z.string(),
  contact_name: z.string(),
  clear_days: z.number().int().positive(),
  /** True once STRIPE_SECRET_KEY is set on the Worker. The pay page hides the
   *  card option entirely when false, so an unconfigured deploy can't offer a
   *  button that 501s. Defaults false for older API builds. */
  card_enabled: z.boolean().default(false),
  /** Host-configured display order of the payment options, already filtered
   *  to what's actually offerable (card omitted unless card_enabled). */
  method_order: z.array(PayMethod).default(PAY_METHOD_DEFAULT_ORDER),
  /** Stripe publishable key for the embedded card form. Safe to expose — it
   *  can only create tokens, not move money. Null when card is off, in which
   *  case the form is never mounted. */
  stripe_publishable_key: z.string().nullable().default(null),
});
export type PaymentConfig = z.infer<typeof PaymentConfig>;

/* ===========================================================================
 * Admin list query
 * ========================================================================= */

export const EnrolmentListQuery = z.object({
  payment_status: PaymentStatus.optional(),
  /**
   * Include UNSUBMITTED registrations — forms filled but never committed.
   *
   * Off by default, because they are not work to chase and they drown the
   * inbox. On when the host needs them, and they do sometimes: a parent can
   * pay mid-form and never finish, and the host wants to find that row and
   * tell them to resume.
   */
  include_unsubmitted: z.boolean().optional(),
  /**
   * Include CLOSED registrations — ones the host withdrew because the family
   * pulled out or the row was never really a registration.
   *
   * Off by default. Everything else that counts registrations (the roster, the
   * register, invoices, the sibling-discount ladder) already excludes them, so
   * an inbox that still listed them was the one place a closed row was counted
   * — "12 shown" meant nine families and three who had pulled out. On when the
   * host needs to find one again, chiefly to reopen it.
   */
  include_closed: z.boolean().optional(),
  /**
   * Include registrations belonging to ARCHIVED programs — the finished
   * holiday club, last term's class.
   *
   * Off by default, and this is the whole point of archiving: a registration
   * for something that already happened is not work, and leaving it in the
   * inbox is how a host ends up scrolling past September to find October. On
   * when they want the history back, which the screen offers as one toggle.
   */
  include_archived_programs: z.boolean().optional(),
});
export type EnrolmentListQuery = z.infer<typeof EnrolmentListQuery>;

/* ===========================================================================
 * Exclusion tags (migration 0045)
 * ========================================================================= */

/**
 * A label the HOST invents and sticks on a child: "Term 4 sport", "Away this
 * term", "Year 6 camp".
 *
 * It means "don't count this child in for that" — so a tagged child drops out
 * of a mail-out about it, and out of the list of children who are free to
 * enrol. Unlike `PHOTO_EXCLUSION_LABELS`, whose three codes live in this file
 * and need a developer to extend, these are rows: the host adds "Term 4 sport"
 * in the portal and uses it the same minute.
 */
export const ExclusionTag = z.object({
  id: z.number().int(),
  /** What the host typed, shown everywhere. */
  label: z.string().min(1),
  /** Normalised label — the uniqueness key, never displayed. */
  slug: z.string().min(1),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  created_by: z.string().nullable().default(null),
  /** How many children currently carry this tag, for the manage list. */
  child_count: z.number().int().default(0),
});
export type ExclusionTag = z.infer<typeof ExclusionTag>;

export const ExclusionTagInput = z.object({
  label: z.string().trim().min(1).max(60),
  sort_order: z.number().int().default(0),
});
export type ExclusionTagInput = z.infer<typeof ExclusionTagInput>;

/**
 * Normalise a tag label into its uniqueness key.
 *
 * Lower-cased, punctuation dropped, runs of whitespace collapsed — so "Term 4
 * Sport", "term 4 sport" and "Term-4 Sport!" are one tag rather than three
 * lists that each hold part of the answer. Exported because the server writes
 * the slug and the portal needs to warn about a duplicate BEFORE saving.
 */
export function exclusionTagSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "-");
}

/* ===========================================================================
 * Broadcasts — host-written messages to families (migration 0045)
 * ========================================================================= */

/**
 * WHO a message goes to.
 *
 *   enrolled — families with a registration in a LIVE (non-archived) program.
 *              Optionally narrowed to one program.
 *   past     — families who registered for something that is now ARCHIVED and
 *              have nothing live. The "you came last term, come back" list.
 *   all      — every family on record, live or lapsed.
 *
 * `past` is defined against archiving rather than against dates on purpose: the
 * host decides when a program is over (see `Program.archived_at`), so the list
 * can never disagree with what they see in the portal.
 */
export const BroadcastScope = z.enum(["enrolled", "past", "all"]);
export type BroadcastScope = z.infer<typeof BroadcastScope>;

export const BroadcastAudience = z.object({
  scope: BroadcastScope.default("enrolled"),
  /** Narrow `enrolled` to one program. Null = every live program. Ignored by
   *  the other scopes, which are not about a single program. */
  program_id: z.number().int().nullable().default(null),
  /** Leave out any child carrying one of these tags (see ExclusionTag). */
  exclude_tag_ids: z.array(z.number().int()).max(50).default([]),
});
export type BroadcastAudience = z.infer<typeof BroadcastAudience>;

/** One child as the audience preview lists them. */
export const BroadcastChild = z.object({
  id: z.number().int(),
  name: z.string(),
  year_level: z.string().nullable().default(null),
  /** The tag labels that excluded this child, when they were excluded. */
  excluded_by: z.array(z.string()).default([]),
});
export type BroadcastChild = z.infer<typeof BroadcastChild>;

/**
 * One FAMILY in the audience — the unit a message is actually sent to, because
 * the address belongs to the parent rather than to any one child.
 *
 * `children` are the ones free to be included; `excluded_children` are the ones
 * a tag took out. A family appears here as long as at least ONE child is free:
 * the host's rule is "only skip them if none of the kids are available", so a
 * household with one child at sport and one at a loose end still hears from us,
 * and the message can honestly name only the child it is about.
 */
export const BroadcastRecipient = z.object({
  parent_email: z.string(),
  parent_name: z.string().default(""),
  children: z.array(BroadcastChild).default([]),
  excluded_children: z.array(BroadcastChild).default([]),
});
export type BroadcastRecipient = z.infer<typeof BroadcastRecipient>;

/**
 * The answer to "who would this go to?", shown BEFORE anything is sent.
 *
 * The counts are as important as the list: a host who sees "4 families left
 * out — every child tagged" can tell the difference between a small audience
 * and a broken filter, which a bare list of names cannot.
 */
/**
 * What is left of today's email allowance.
 *
 * The provider's free tier caps EVERYTHING the site sends — receipts,
 * invoices, sign-in links and mail-outs out of one pot — so a mail-out has to
 * be told what it may spend. `reserve` is held back for the automatic emails
 * and never offered to the host, because a receipt for money someone just paid
 * matters more than a newsletter.
 */
export const EmailBudget = z.object({
  limit: z.number().int().default(0),
  used_today: z.number().int().default(0),
  reserve: z.number().int().default(0),
  available_for_broadcast: z.number().int().default(0),
  /** Busiest day of automatic email in the recent past — what the reserve is
   *  sized from, rather than a number somebody picked. */
  busiest_day: z.number().int().default(0),
  /** Mean automatic emails on a day that sent any. */
  typical_day: z.number().int().default(0),
  /** How many days of history those two are drawn from. */
  days_measured: z.number().int().default(0),
});
export type EmailBudget = z.infer<typeof EmailBudget>;

export const BroadcastPreview = z.object({
  recipients: z.array(BroadcastRecipient).default([]),
  /** Families where EVERY child was tagged, so nobody was left to write to. */
  skipped_all_children_excluded: z.number().int().default(0),
  /** Families who have asked not to receive these (email_optouts). */
  skipped_unsubscribed: z.number().int().default(0),
  /** Children free to take part, across every listed family — the host's
   *  "who could I enrol?" number. */
  available_children: z.number().int().default(0),
  /** Today's remaining email allowance, so the screen can say up front when an
   *  audience is larger than what is safely left to send. */
  budget: EmailBudget.default({
    limit: 0,
    used_today: 0,
    reserve: 0,
    available_for_broadcast: 0,
    busiest_day: 0,
    typical_day: 0,
    days_measured: 0,
  }),
});
export type BroadcastPreview = z.infer<typeof BroadcastPreview>;

/**
 * The placeholders a host may use in a message. Deliberately tiny and
 * family-level — a message goes to a household, so there is no single child to
 * speak about, and `{{child_names}}` is the list of that family's AVAILABLE
 * children ("Ava and Sam"), never the excluded ones.
 */
export const BROADCAST_PLACEHOLDERS: Record<string, string> = {
  "{{parent_name}}": "the parent's first name",
  "{{child_names}}": "their children who are free to take part",
  "{{child_count}}": "how many of their children that is",
};

export const BroadcastInput = z.object({
  subject: z.string().trim().min(1).max(200),
  /** The message body as the host typed it: plain text with blank lines
   *  between paragraphs, rendered into the house email layout on send. */
  body: z.string().trim().min(1).max(20000),
  audience: BroadcastAudience,
});
export type BroadcastInput = z.infer<typeof BroadcastInput>;

/** A mail-out that was sent — the history list. */
export const Broadcast = z.object({
  id: z.number().int(),
  subject: z.string(),
  body: z.string().default(""),
  audience: BroadcastAudience,
  sent_at: z.string(),
  sent_by: z.string().nullable().default(null),
  recipient_count: z.number().int().default(0),
  failed_count: z.number().int().default(0),
});
export type Broadcast = z.infer<typeof Broadcast>;

/** What came back from a send. `failed` is reported rather than thrown: one
 *  bounced address must not look like the whole mail-out failing. */
export const BroadcastResult = z.object({
  id: z.number().int(),
  sent: z.number().int().default(0),
  failed: z.number().int().default(0),
  /** Addresses that errored, so the host can chase or correct them. */
  errors: z.array(z.object({ email: z.string(), error: z.string() })).default([]),
});
export type BroadcastResult = z.infer<typeof BroadcastResult>;

/* ===========================================================================
 * Unsubscribe (migration 0045)
 * ========================================================================= */

/** POST /api/unsubscribe — public, no session. The token is an HMAC of the
 *  address, so the link in an email can only ever unsubscribe THAT address. */
export const UnsubscribeInput = z.object({
  email: z.string().trim().min(1).max(320),
  token: z.string().trim().min(1).max(200),
});
export type UnsubscribeInput = z.infer<typeof UnsubscribeInput>;

export const UnsubscribeResult = z.object({
  ok: z.literal(true),
  /** Echoed back so the page can say "you" rather than "this address". */
  email: z.string(),
  /** True when they were already off the list — the page then says so instead
   *  of implying something changed. */
  already: z.boolean().default(false),
});
export type UnsubscribeResult = z.infer<typeof UnsubscribeResult>;

/* ===========================================================================
 * Live spreadsheet feeds (migration 0042)
 * ========================================================================= */

/**
 * A standing link that answers with the registration spreadsheet as CSV, so a
 * Google Sheet can pull the current registrations instead of the host
 * exporting and re-pasting a file every time.
 *
 * The secret is IN THE LINK (`?key=`), because the thing fetching it is a
 * spreadsheet: it cannot sign in, hold a cookie, or pass through Cloudflare
 * Access. So the link is a capability — anyone holding it can read those
 * registrations — which is why `label`, `created_by` and `last_used_at` exist
 * (a host must be able to see what they handed out and when it was last read)
 * and why rotating or deleting it is one click on the Submissions screen.
 */
export const EnrolmentFeed = z.object({
  id: z.number().int(),
  /** What the host called it — "Term 4 drama", "Committee copy". */
  label: z.string().default(""),
  /** Which program's registrations it carries; null = every program. Held as a
   *  string to match `enrolments.program_id`. */
  program_id: z.string().nullable().default(null),
  /** The program's title at the time of reading, for display only. */
  program_title: z.string().nullable().default(null),
  /** The full, ready-to-paste URL including the key. */
  url: z.string(),
  created_at: z.string(),
  created_by: z.string().nullable().default(null),
  /** When a spreadsheet last pulled it — null means never opened. */
  last_used_at: z.string().nullable().default(null),
});
export type EnrolmentFeed = z.infer<typeof EnrolmentFeed>;

export const EnrolmentFeedInput = z.object({
  label: z.string().max(80).default(""),
  program_id: z.string().nullable().default(null),
});
export type EnrolmentFeedInput = z.infer<typeof EnrolmentFeedInput>;

/* ===========================================================================
 * Typed fetch client
 * ========================================================================= */

export interface ApiClientOptions {
  /** Extra headers merged into every request (e.g. CF Access cookie passthrough). */
  headers?: Record<string, string>;
  /** Custom fetch impl (defaults to global fetch). */
  fetch?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function apiClient(baseUrl: string, opts: ApiClientOptions = {}) {
  const f = opts.fetch ?? fetch;
  const base = baseUrl.replace(/\/$/, "");

  async function req<S extends z.ZodTypeAny>(
    method: string,
    path: string,
    schema: S,
    body?: unknown,
    init?: RequestInit,
  ): Promise<z.infer<S>> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(opts.headers ?? {}),
      ...((init?.headers as Record<string, string>) ?? {}),
    };
    if (body !== undefined && !(body instanceof Blob) && !(body instanceof ArrayBuffer)) {
      headers["Content-Type"] = "application/json";
    }
    const res = await f(`${base}${path}`, {
      method,
      headers,
      body:
        body === undefined
          ? undefined
          : body instanceof Blob || body instanceof ArrayBuffer
            ? body
            : JSON.stringify(body),
      ...init,
    });
    const text = await res.text();
    const json = text ? safeJson(text) : undefined;
    if (!res.ok) {
      /* Prefer the SERVER'S own sentence. Handlers here write refusals meant to
         be read by a non-technical host ("that address already belongs to
         another family…"), and every one of them used to be thrown away in
         favour of "PATCH /api/… failed (409)" — which tells the person on the
         screen nothing about what to do next. The method/path form remains the
         fallback for a response that carries no message. */
      const detail =
        json && typeof json === "object" && typeof (json as { error?: unknown }).error === "string"
          ? (json as { error: string }).error.trim()
          : "";
      throw new ApiError(res.status, detail || `${method} ${path} failed (${res.status})`, json);
    }
    return schema.parse(json);
  }

  function qs(params: Record<string, string | undefined>): string {
    const entries = Object.entries(params).filter(([, v]) => v != null && v !== "");
    if (entries.length === 0) return "";
    const u = new URLSearchParams(entries as [string, string][]);
    return `?${u.toString()}`;
  }

  return {
    /** POST /api/enrol */
    enrol: (input: EnrolmentInput) =>
      req("POST", "/api/enrol", EnrolmentCreated, EnrolmentInput.parse(input)),

    /** GET /api/late-link/check — is this `?code=` good for this program, for
     *  whoever is signed in? Decides whether the journey shows a form or the
     *  "closed" card, and what it says; the actual gate is re-checked
     *  server-side on submit, against the verified session email. */
    lateLinkCheck: (programId: number, code: string) =>
      req(
        "GET",
        `/api/late-link/check${qs({ program_id: String(programId), code })}`,
        LateLinkCheck,
      ),

    /* ---- auth (magic-link sign-in; cookies ride same-origin fetch) ---- */

    /** POST /api/auth/request-link — email a one-time sign-in link. */
    requestLink: (input: AuthRequestLinkInput) =>
      req("POST", "/api/auth/request-link", AuthRequestLinkResult, AuthRequestLinkInput.parse(input)),

    /** POST /api/auth/verify — consume a link token; sets the session cookie. */
    verifyMagicToken: (token: string) =>
      req("POST", "/api/auth/verify", AuthVerifyResult, { token }),

    /** GET /api/auth/me — current session email (null when signed out). */
    authMe: () => req("GET", "/api/auth/me", AuthMe),

    /** POST /api/auth/signout — clear the session cookie. */
    signOut: () => req("POST", "/api/auth/signout", z.object({ ok: z.literal(true) })),

    /** GET /api/my/enrolments — the signed-in parent's registrations. */
    myEnrolments: () => req("GET", "/api/my/enrolments", z.array(Enrolment)),

    /** PATCH /api/enrolments/:ref — session-owned journey updates. */
    updateEnrolment: (ref: string, patch: EnrolmentUpdate) =>
      req("PATCH", `/api/enrolments/${encodeURIComponent(ref)}`, Enrolment, EnrolmentUpdate.parse(patch)),

    /** GET /api/my/profile — saved parent details + previously enrolled
     *  children (prefills the /enrol journey). */
    myProfile: () => req("GET", "/api/my/profile", MyProfile),

    /** PUT /api/my/profile — save the parent's name/phone (account page). */
    updateMyProfile: (input: ParentProfileInput) =>
      req("PUT", "/api/my/profile", ParentProfile, ParentProfileInput.parse(input)),

    /** GET /api/my/draft — the signed-in parent's saved journey (404→null). */
    getDraft: () =>
      req("GET", "/api/my/draft", EnrolmentDraft.nullable()),

    /** PUT /api/my/draft — autosave the in-progress journey. */
    putDraft: (data: Record<string, unknown>) =>
      req("PUT", "/api/my/draft", EnrolmentDraft, { data }),

    /** DELETE /api/my/draft — clear after successful submission. */
    deleteDraft: () =>
      req("DELETE", "/api/my/draft", z.object({ ok: z.literal(true) })),

    /** POST /api/contact */
    contact: (input: ContactMessageInput) =>
      req("POST", "/api/contact", ContactMessage, ContactMessageInput.parse(input)),

    /** POST /api/payments/intent */
    paymentIntent: (input: PaymentIntentInput) =>
      req("POST", "/api/payments/intent", PaymentIntentResult, PaymentIntentInput.parse(input)),

    /** GET /api/payments/:ref/summary — amount due + status for the pay page. */
    paymentSummary: (ref: string) =>
      req("GET", `/api/payments/${encodeURIComponent(ref)}/summary`, PaymentSummary),

    /** POST /api/payments/checkout — open a Stripe card payment; the caller
     *  redirects to the returned URL. */
    paymentCheckout: (input: CheckoutSessionInput) =>
      req("POST", "/api/payments/checkout", CheckoutSessionResult, CheckoutSessionInput.parse(input)),

    /** GET /api/payments/group-summary — what one grouped payment would cover
     *  for the signed-in parent (count 0 when signed out or nothing owing). */
    paymentGroupSummary: () => req("GET", "/api/payments/group-summary", GroupSummary),

    /** POST /api/invoices — issue an invoice over the caller's own enrolments. */
    createInvoice: (input: InvoiceCreateInput) =>
      req("POST", "/api/invoices", Invoice, InvoiceCreateInput.parse(input)),

    /** GET /api/my/invoices — the signed-in parent's invoices, newest first. */
    myInvoices: () => req("GET", "/api/my/invoices", z.array(Invoice)),

    /** GET /api/admin/invoices — every invoice, newest first [Access].
     *  `status` filters; omit for all. */
    adminListInvoices: (status?: InvoiceStatus) =>
      req("GET", `/api/admin/invoices${qs({ status })}`, z.array(Invoice)),

    /** GET /api/admin/invoices/:id — one invoice with its lines [Access]. */
    adminGetInvoice: (id: number) => req("GET", `/api/admin/invoices/${id}`, Invoice),

    /** GET /api/admin/emails — every email the site has sent, newest first
     *  [Access]. `kind` groups them, `ref` pins one payment record, `q`
     *  matches recipient or subject. */
    adminListEmails: (params?: { kind?: string; ref?: string; q?: string }) =>
      req("GET", `/api/admin/emails${qs(params ?? {})}`, z.array(EmailLogEntry)),

    /** POST /api/admin/invoices/:id/send — email it to the family it bills.
     *  The address comes from the invoice, never from the caller. */
    adminSendInvoice: (id: number) =>
      req("POST", `/api/admin/invoices/${id}/send`, InvoiceSendResult),

    /** POST /api/payments/checkout/group — one card payment for ALL of the
     *  signed-in parent's outstanding enrolments. */
    paymentCheckoutGroup: () =>
      req("POST", "/api/payments/checkout/group", GroupCheckoutResult),

    /** GET /api/forms/:formKey */
    getForm: (formKey: string) =>
      req("GET", `/api/forms/${encodeURIComponent(formKey)}`, z.array(FormField)),

    /** GET /api/programs — active programs for the public site */
    getPrograms: () => req("GET", "/api/programs", z.array(Program)),

    /** GET /api/payment-config — bank details + clearing window for the
     *  public payment pages (host-editable via admin settings). */
    paymentConfig: () => req("GET", "/api/payment-config", PaymentConfig),

    /** GET /api/admin/settings — the full settings catalogue with effective
     *  values [Access]. */
    adminListSettings: () => req("GET", "/api/admin/settings", z.array(AdminSetting)),

    /** PUT /api/admin/settings — save one setting; null/empty value reverts
     *  it to the default [Access]. */
    adminSaveSetting: (input: AdminSettingSaveInput) =>
      req("PUT", "/api/admin/settings", AdminSetting, AdminSettingSaveInput.parse(input)),

    /** GET /api/admin/programs — every program [Access] */
    adminListPrograms: () => req("GET", "/api/admin/programs", z.array(Program)),

    /** POST /api/admin/programs — create (omit id) or update (pass id) [Access] */
    adminUpsertProgram: (input: ProgramInput, id?: number) =>
      req(
        "POST",
        "/api/admin/programs",
        Program,
        id !== undefined ? { ...ProgramInput.parse(input), id } : ProgramInput.parse(input),
      ),

    /** DELETE /api/admin/programs/:id [Access] */
    adminDeleteProgram: (id: number) =>
      req("DELETE", `/api/admin/programs/${id}`, z.object({ ok: z.boolean() })),

    /** PATCH /api/admin/programs/:id/archive — file a finished program away
     *  (its registrations leave the Submissions inbox and its families become
     *  the "past" mailing audience) or restore it. Soft either way: nothing is
     *  deleted and no email is sent. [Access] */
    adminArchiveProgram: (id: number, archived: boolean) =>
      req("PATCH", `/api/admin/programs/${id}/archive`, Program, { archived }),

    /* ---- exclusion tags + messaging (migration 0045) [Access] -------------- */

    /** GET /api/admin/exclusion-tags — the host's own labels, with how many
     *  children carry each. */
    adminListExclusionTags: () =>
      req("GET", "/api/admin/exclusion-tags", z.array(ExclusionTag)),

    /** POST /api/admin/exclusion-tags — create (omit id) or rename (pass id).
     *  Renaming keeps every child already tagged. */
    adminSaveExclusionTag: (input: ExclusionTagInput, id?: number) =>
      req(
        "POST",
        "/api/admin/exclusion-tags",
        ExclusionTag,
        id !== undefined ? { ...ExclusionTagInput.parse(input), id } : ExclusionTagInput.parse(input),
      ),

    /** DELETE /api/admin/exclusion-tags/:id — removes the label and takes it
     *  off every child. The children themselves are untouched. */
    adminDeleteExclusionTag: (id: number) =>
      req("DELETE", `/api/admin/exclusion-tags/${id}`, z.object({ ok: z.boolean() })),

    /** POST /api/admin/broadcasts/preview — who WOULD this go to? Sends
     *  nothing; the screen calls it on every change to the audience picker. */
    adminPreviewBroadcast: (audience: BroadcastAudience) =>
      req(
        "POST",
        "/api/admin/broadcasts/preview",
        BroadcastPreview,
        BroadcastAudience.parse(audience),
      ),

    /** POST /api/admin/broadcasts — write the message to every family in the
     *  audience. The audience is re-resolved server-side, so what was previewed
     *  cannot drift from what is sent. */
    adminSendBroadcast: (input: BroadcastInput) =>
      req("POST", "/api/admin/broadcasts", BroadcastResult, BroadcastInput.parse(input)),

    /** GET /api/admin/broadcasts — what has been sent, newest first. */
    adminListBroadcasts: () => req("GET", "/api/admin/broadcasts", z.array(Broadcast)),

    /** POST /api/unsubscribe — PUBLIC, no session. Takes a family off the
     *  mail-out list. The token is an HMAC of the address from their own
     *  email, so this can only ever unsubscribe the person who was written to.
     *  Receipts, invoices and sign-in links are unaffected. */
    unsubscribe: (input: UnsubscribeInput) =>
      req("POST", "/api/unsubscribe", UnsubscribeResult, UnsubscribeInput.parse(input)),

    /* ---- the private "get past the cut-off" links, one per family [Access] -- */

    /** GET /api/admin/programs/:id/late-invites — newest first, revoked and
     *  expired ones included (the host wants to see what they sent). */
    adminProgramLateInvites: (programId: number) =>
      req(
        "GET",
        `/api/admin/programs/${programId}/late-invites`,
        z.object({ invites: z.array(ProgramLateInvite) }),
      ),

    /** POST /api/admin/programs/:id/late-invites — make one for ONE family.
     *  Adds to the list; it never replaces an existing invite. */
    adminCreateProgramLateInvite: (programId: number, input: { email: string; note?: string }) =>
      req(
        "POST",
        `/api/admin/programs/${programId}/late-invites`,
        z.object({ invite: ProgramLateInvite }),
        input,
      ),

    /** DELETE /api/admin/programs/:id/late-invites/:inviteId — turn ONE invite
     *  off without touching the others. Idempotent. */
    adminRevokeProgramLateInvite: (programId: number, inviteId: number) =>
      req(
        "DELETE",
        `/api/admin/programs/${programId}/late-invites/${inviteId}`,
        z.object({ ok: z.boolean() }),
      ),

    /* ---- attendance (Access-gated) ---- */

    /** GET /api/admin/attendance/sessions?program_id= — the program's lessons,
     *  newest lesson first. Ensures the weekly lessons up to today exist first
     *  (auto-generated while the program is active). [Access] */
    adminListSessions: (programId: number) =>
      req(
        "GET",
        `/api/admin/attendance/sessions${qs({ program_id: String(programId) })}`,
        z.array(AttendanceSessionSummary),
      ),

    /** POST /api/admin/attendance/sessions — add a lesson. Omit session_date to
     *  append the next weekly lesson after the latest one ("prepare" ahead). [Access] */
    adminAddSession: (input: AddSessionInput) =>
      req("POST", "/api/admin/attendance/sessions", ProgramSession, AddSessionInput.parse(input)),

    /** PATCH /api/admin/attendance/sessions/:id — lesson title/notes/status. [Access] */
    adminUpdateSession: (id: number, input: SessionUpdateInput) =>
      req(
        "PATCH",
        `/api/admin/attendance/sessions/${id}`,
        ProgramSession,
        SessionUpdateInput.parse(input),
      ),

    /** GET /api/admin/attendance/sheet?session_id= — a lesson + its roster/marks. [Access] */
    adminGetAttendanceSheet: (sessionId: number) =>
      req(
        "GET",
        `/api/admin/attendance/sheet${qs({ session_id: String(sessionId) })}`,
        AttendanceSheet,
      ),

    /** POST /api/admin/attendance/mark — record (status) or clear (status:null)
     *  one student's mark for one lesson. [Access] */
    adminMarkAttendance: (input: MarkAttendanceInput) =>
      req("POST", "/api/admin/attendance/mark", AttendanceMark, MarkAttendanceInput.parse(input)),

    /** GET /api/admin/attendance/summary?program_id= — per-program report. [Access] */
    adminAttendanceSummary: (programId: number) =>
      req(
        "GET",
        `/api/admin/attendance/summary${qs({ program_id: String(programId) })}`,
        AttendanceSummary,
      ),

    /** GET /api/admin/attendance/grid?program_id= — whole term as a matrix
     *  (lessons × students) for the read-only spreadsheet view. [Access] */
    adminAttendanceGrid: (programId: number) =>
      req(
        "GET",
        `/api/admin/attendance/grid${qs({ program_id: String(programId) })}`,
        AttendanceGrid,
      ),

    /* ---- admin (Cloudflare Access-gated) ---- */

    /** GET /api/admin/enrolments?payment_status= */
    listEnrolments: (query: EnrolmentListQuery = {}) =>
      req(
        "GET",
        `/api/admin/enrolments${qs({
        payment_status: query.payment_status,
        include_unsubmitted: query.include_unsubmitted ? "1" : undefined,
        include_closed: query.include_closed ? "1" : undefined,
        include_archived_programs: query.include_archived_programs ? "1" : undefined,
      })}`,
        z.array(Enrolment),
      ),

    /** GET /api/admin/children — every child record with parent details and
     *  enrolment aggregates [Access]. */
    adminListChildren: () => req("GET", "/api/admin/children", z.array(AdminChild)),

    /* ---- live spreadsheet feeds (0042) ---- */

    /** GET /api/admin/enrolment-feeds — every standing sheet link, with its
     *  full url. [Access] */
    adminListEnrolmentFeeds: () =>
      req("GET", "/api/admin/enrolment-feeds", z.array(EnrolmentFeed)),

    /** POST /api/admin/enrolment-feeds — mint a new link. [Access] */
    adminCreateEnrolmentFeed: (input: EnrolmentFeedInput) =>
      req(
        "POST",
        "/api/admin/enrolment-feeds",
        EnrolmentFeed,
        EnrolmentFeedInput.parse(input),
      ),

    /** POST /api/admin/enrolment-feeds/:id/rotate — issue a fresh key for the
     *  same link, instantly killing the old one. [Access] */
    adminRotateEnrolmentFeed: (id: number) =>
      req("POST", `/api/admin/enrolment-feeds/${id}/rotate`, EnrolmentFeed),

    /** DELETE /api/admin/enrolment-feeds/:id — stop the link working. [Access] */
    adminDeleteEnrolmentFeed: (id: number) =>
      req("DELETE", `/api/admin/enrolment-feeds/${id}`, z.object({ ok: z.literal(true) })),

    /** POST /api/admin/enrolments — manually enrol a student into a program
     *  (walk-up on the day, or an existing family re-enrolling for the term).
     *  Returns the created enrolment, and whether the parent was emailed. [Access] */
    adminCreateEnrolment: (input: AdminEnrolmentInput) =>
      req("POST", "/api/admin/enrolments", AdminEnrolmentCreated, AdminEnrolmentInput.parse(input)),

    /** PATCH /api/admin/enrolments/:id — correct what a family submitted (names,
     *  year level, form answers). Payment fields are not editable here. [Access] */
    adminUpdateEnrolment: (id: number, input: EnrolmentUpdateInput) =>
      req("PATCH", `/api/admin/enrolments/${id}`, Enrolment, EnrolmentUpdateInput.parse(input)),

    /** PATCH /api/admin/parents/:email — correct a family's contact details.
     *  Changing the email re-keys their children + enrolments. [Access] */
    adminUpdateParent: (email: string, input: ParentUpdateInput) =>
      req(
        "PATCH",
        `/api/admin/parents/${encodeURIComponent(email)}`,
        AdminParentUpdateResult,
        ParentUpdateInput.parse(input),
      ),

    /* ---- moving ONE registration to another address (0039) ---- */

    /** GET /api/admin/enrolments/:id/move/plan?to=… — what a move to that
     *  address WOULD do. Reads only; safe to call as the host types. [Access] */
    adminEnrolmentMovePlan: (id: number, toEmail: string) =>
      req(
        "GET",
        `/api/admin/enrolments/${id}/move/plan?to=${encodeURIComponent(toEmail)}`,
        EnrolmentMovePlan,
      ),

    /** POST /api/admin/enrolments/:id/move — ask the destination address to
     *  confirm. NOTHING moves until they click the emailed link. [Access] */
    adminRequestEnrolmentMove: (id: number, input: EnrolmentMoveRequestInput) =>
      req(
        "POST",
        `/api/admin/enrolments/${id}/move`,
        EnrolmentMoveRequested,
        EnrolmentMoveRequestInput.parse(input),
      ),

    /** GET /api/admin/enrolment-moves — every move, or just the ones still
     *  awaiting a click (`open`), or just one registration's. [Access] */
    adminListEnrolmentMoves: (params: { enrolment_id?: number; open?: boolean } = {}) =>
      req(
        "GET",
        `/api/admin/enrolment-moves${qs({
          enrolment_id: params.enrolment_id ? String(params.enrolment_id) : undefined,
          open: params.open ? "1" : undefined,
        })}`,
        z.array(EnrolmentMove),
      ),

    /** POST /api/admin/enrolment-moves/:id/cancel — withdraw the request; the
     *  emailed link stops working immediately. [Access] */
    adminCancelEnrolmentMove: (id: number) =>
      req("POST", `/api/admin/enrolment-moves/${id}/cancel`, EnrolmentMove, {}),

    /** POST /api/admin/enrolment-moves/:id/resend — a fresh link and a fresh
     *  expiry, for a request that was never opened. [Access] */
    adminResendEnrolmentMove: (id: number) =>
      req("POST", `/api/admin/enrolment-moves/${id}/resend`, EnrolmentMoveRequested, {}),

    /** POST /api/enrolment-moves/invite — describe the emailed link WITHOUT
     *  using it up, so the page can say what is being confirmed. [public] */
    enrolmentMoveInvite: (token: string) =>
      req("POST", "/api/enrolment-moves/invite", EnrolmentMoveInvite, { token }),

    /** POST /api/enrolment-moves/confirm — the destination address agrees, and
     *  the move is applied. One-time. [public] */
    confirmEnrolmentMove: (token: string) =>
      req("POST", "/api/enrolment-moves/confirm", EnrolmentMoveConfirmed, { token }),

    /** GET /api/admin/record-edits — the correction history for one record,
     *  newest first. [Access] */
    adminRecordEdits: (entity: RecordEdit["entity"], entityId: string | number) =>
      req(
        "GET",
        `/api/admin/record-edits?entity=${encodeURIComponent(entity)}&entity_id=${encodeURIComponent(String(entityId))}`,
        z.array(RecordEdit),
      ),

    /** PATCH /api/admin/children/:id — edit a student's details (year level,
     *  medical/allergy note, photo permission) [Access]. */
    adminUpdateChild: (id: number, input: ChildUpdateInput) =>
      req("PATCH", `/api/admin/children/${id}`, AdminChild, ChildUpdateInput.parse(input)),

    /** POST /api/admin/children/year-levels — apply the start-of-school-year
     *  review in one pass. Every change is recorded in the correction history
     *  and reaches the attendance roll immediately. [Access] */
    adminReviewYearLevels: (input: YearLevelReviewInput) =>
      req(
        "POST",
        "/api/admin/children/year-levels",
        YearLevelReviewResult,
        YearLevelReviewInput.parse(input),
      ),

    /** PATCH /api/admin/children/:id/archive — archive (hide from the active
     *  list, keep email + history) or restore a student record [Access]. */
    adminArchiveChild: (id: number, archived: boolean) =>
      req("PATCH", `/api/admin/children/${id}/archive`, AdminChild, { archived }),

    /** DELETE /api/admin/children/:id — permanently remove a student record and
     *  its enrolment / attendance / payment rows (for test data) [Access]. */
    adminDeleteChild: (id: number) =>
      req("DELETE", `/api/admin/children/${id}`, DeleteChildResult),

    /** DELETE /api/admin/enrolments/:id — permanently remove one registration
     *  and everything hanging off it (attendance, payments, invoice line, move
     *  requests, correction history); the child record is kept because it
     *  usually has other registrations. For test data, not withdrawals. [Access] */
    adminDeleteEnrolment: (id: number) =>
      req("DELETE", `/api/admin/enrolments/${id}`, DeleteEnrolmentResult),

    /** GET /api/admin/db-dump — generic dump of every user table (raw rows;
     *  the Studio sidecar's sandbox hydration source). [Access] */
    adminDbDump: () => req("GET", "/api/admin/db-dump", AdminDbDump),

    /** POST /api/admin/import — idempotent bulk record import (no emails, no
     *  side effects); rows link by natural keys [Access]. */
    adminImportRecords: (input: ImportRequest) =>
      req("POST", "/api/admin/import", ImportResult, ImportRequest.parse(input)),

    /** POST /api/admin/payments/:id/confirm */
    confirmPayment: (id: number) =>
      req("POST", `/api/admin/payments/${id}/confirm`, PaymentConfirmResult),

    /** POST /api/admin/payments/:id/notify-am — email the account manager a
     *  magic link straight to this record's reconcile page. [Access] */
    notifyAccountManager: (id: number) =>
      req(
        "POST",
        `/api/admin/payments/${id}/notify-am`,
        z.object({ ok: z.literal(true), emailed: z.boolean() }),
      ),

    /** GET /api/admin/reconcile/enrolments — admin-portal alias (Access). */
    adminReconcileList: (query: EnrolmentListQuery = {}) =>
      req(
        "GET",
        `/api/admin/reconcile/enrolments${qs({ payment_status: query.payment_status })}`,
        z.array(ReconcileEnrolment),
      ),

    /** GET /api/admin/reconcile/enrolments/:ref — admin-portal alias. */
    adminReconcileGet: (ref: string) =>
      req("GET", `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}`, ReconcileDetail),

    /** POST /api/admin/reconcile/enrolments/:ref/payments — admin-portal alias. */
    adminReconcileRecordPayment: (ref: string, input: PaymentRecordInput) =>
      req(
        "POST",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/payments`,
        ReconcileDetail,
        PaymentRecordInput.parse(input),
      ),

    /** POST /api/admin/reconcile/enrolments/:ref/request-payment — email this
     *  one family a request for what they still owe, in the host's words. */
    adminRequestPayment: (ref: string, input: PaymentRequestInput) =>
      req(
        "POST",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/request-payment`,
        PaymentRequestResult,
        PaymentRequestInput.parse(input),
      ),

    /** POST /api/admin/reconcile/enrolments/:ref/request-family-payment — email
     *  this family ONE request covering every registration under their address
     *  that still owes money. `ref` may be any of their registrations; the
     *  family is resolved from it server-side. Already-settled siblings are
     *  shown on the email but never charged for. */
    adminRequestFamilyPayment: (ref: string, input: PaymentRequestInput) =>
      req(
        "POST",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/request-family-payment`,
        FamilyPaymentRequestResult,
        PaymentRequestInput.parse(input),
      ),

    /** PATCH /api/admin/reconcile/enrolments/:ref/fee — correct what this
     *  registration is CHARGED. The status follows the corrected fee, so a
     *  record wrongly showing "Paid" (a waiver that shouldn't have applied)
     *  goes back to unpaid and can be chased like any other. */
    adminReconcileAdjustFee: (ref: string, input: FeeAdjustInput) =>
      req(
        "PATCH",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/fee`,
        ReconcileDetail,
        FeeAdjustInput.parse(input),
      ),

    /** DELETE /api/admin/reconcile/enrolments/:ref/payments/:id — remove a
     *  mistakenly recorded payment; the enrolment's status drops back to
     *  partial/unpaid and the removal is kept in the event history. */
    adminReconcileVoidPayment: (ref: string, paymentId: number) =>
      req(
        "DELETE",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/payments/${paymentId}`,
        ReconcileDetail,
      ),

    /** PATCH /api/admin/reconcile/enrolments/:ref/payments/:id — correct a
     *  recorded payment (amount/method/note); the enrolment's status is
     *  recomputed from the corrected ledger (paid/partial/unpaid). */
    adminReconcileUpdatePayment: (ref: string, paymentId: number, input: PaymentRecordInput) =>
      req(
        "PATCH",
        `/api/admin/reconcile/enrolments/${encodeURIComponent(ref)}/payments/${paymentId}`,
        ReconcileDetail,
        PaymentRecordInput.parse(input),
      ),

    /* ---- reconcile (account manager via magic-link session, or admin) ---- */

    /** GET /api/reconcile/enrolments?payment_status= — with balances. */
    reconcileList: (query: EnrolmentListQuery = {}) =>
      req(
        "GET",
        `/api/reconcile/enrolments${qs({ payment_status: query.payment_status })}`,
        z.array(ReconcileEnrolment),
      ),

    /** GET /api/reconcile/enrolments/:ref — record + ledger + history. */
    reconcileGet: (ref: string) =>
      req("GET", `/api/reconcile/enrolments/${encodeURIComponent(ref)}`, ReconcileDetail),

    /** POST /api/reconcile/enrolments/:ref/payments — record money landed
     *  (partial or full; server moves payment_status accordingly). */
    reconcileRecordPayment: (ref: string, input: PaymentRecordInput) =>
      req(
        "POST",
        `/api/reconcile/enrolments/${encodeURIComponent(ref)}/payments`,
        ReconcileDetail,
        PaymentRecordInput.parse(input),
      ),

    /** POST /api/reconcile/summary — email the admin everything recorded
     *  since the last summary ("Done" button). */
    reconcileSummary: () =>
      req("POST", "/api/reconcile/summary", ReconcileSummaryResult),

    /** GET /api/admin/forms */
    adminListForms: (formKey?: string) =>
      req("GET", `/api/admin/forms${qs({ form_key: formKey })}`, z.array(FormField)),

    /** POST /api/admin/forms — omit `id` to create, pass it to UPDATE in place
     *  (the server reads the raw id before validation strips it). */
    adminUpsertForm: (input: FormFieldInput, id?: number) =>
      req("POST", "/api/admin/forms", FormField, {
        ...FormFieldInput.parse(input),
        ...(id !== undefined ? { id } : {}),
      }),

    /** DELETE /api/admin/forms/:id */
    adminDeleteForm: (id: number) =>
      req("DELETE", `/api/admin/forms/${id}`, z.object({ ok: z.boolean() })),

    /** GET /api/admin/contact-messages — the host inbox (newest first). */
    adminListContactMessages: () =>
      req("GET", "/api/admin/contact-messages", z.array(ContactMessage)),

    /** POST /api/admin/contact-messages/:id/status — new|read|replied|archived. */
    adminSetContactStatus: (id: number, status: "new" | "read" | "replied" | "archived") =>
      req("POST", `/api/admin/contact-messages/${id}/status`, ContactMessage, { status }),

    /** GET /api/admin/assets */
    listAssets: () => req("GET", "/api/admin/assets", z.array(Asset)),

    /** PUT /api/assets — raw body upload; metadata via headers/query. */
    uploadAsset: (
      file: Blob | ArrayBuffer,
      meta: { filename: string; category?: string; alt?: string },
    ) =>
      req(
        "PUT",
        `/api/assets${qs({
          filename: meta.filename,
          category: meta.category,
          alt: meta.alt,
        })}`,
        AssetCreated,
        file,
      ),
  };
}

export type ApiClient = ReturnType<typeof apiClient>;

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/* ===========================================================================
 * Program links — the url a family follows to register for ONE program
 *
 * These used to live in @alight/enrolment-ui, which the Worker cannot import
 * (React). The share-card endpoint (GET /api/share/:program) has to resolve
 * exactly the same link the host's "Copy link" button produces, so the logic
 * moved HERE — the one module both the browser and the Worker already share.
 * @alight/enrolment-ui re-exports it, so every existing import still works.
 * ========================================================================= */

/**
 * Title → url-safe kebab-case; "" when the title has no usable characters.
 *
 * READABLE ENROL LINKS — `/enrol?program=holy-trinity-holiday-club-lost-in-space`
 * instead of `/enrol?program=6`. The slug is derived from the program TITLE, so
 * it stays meaningful in a parent's address bar, in a shared link, and in the
 * magic-link redirect that carries the parent back after they verify.
 */
export function programSlug(title: string): string {
  // NFKD splits accented letters into "letter + combining mark", and the
  // non-alphanumeric sweep below then drops the mark — so "Café" → "cafe".
  return title
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The id/title pair every link helper below needs — a full `Program` satisfies it. */
export interface ProgramLinkFields {
  id: number;
  title: string;
  form_key?: string | null;
  brand_key?: string | null;
}

/**
 * The `?program=` value to LINK to a program. Pass every program the site
 * knows about (not just the enrolable ones) so a duplicate title anywhere
 * still forces the unique `<slug>-<id>` form.
 */
export function programParam(
  program: ProgramLinkFields,
  all: readonly ProgramLinkFields[],
): string {
  const slug = programSlug(program.title);
  if (slug === "") return String(program.id);
  const duplicate = all.some((p) => p.id !== program.id && programSlug(p.title) === slug);
  return duplicate ? `${slug}-${program.id}` : slug;
}

/**
 * Resolve a `?program=` value against the programs a visitor may enrol in.
 * Accepts, in priority order: a bare id, an exact title slug, or the
 * `<slug>-<id>` form. Returns undefined when nothing matches (the caller then
 * falls back to the first available program, as it always has).
 */
export function findProgramByParam<T extends ProgramLinkFields>(
  param: string | undefined,
  candidates: readonly T[],
): T | undefined {
  if (param === undefined || param === "") return undefined;

  // A bare numeric id — the pre-slug link format.
  if (/^\d+$/.test(param)) {
    const id = Number(param);
    return candidates.find((p) => p.id === id);
  }

  // An exact title slug. Checked BEFORE the id-suffix form so a title that
  // genuinely ends in digits ("Holiday Club 2026") isn't misread as an id.
  // Lowest id wins if duplicate titles exist and the link omitted the id.
  const exact = candidates
    .filter((p) => programSlug(p.title) === param)
    .sort((a, b) => a.id - b.id);
  if (exact.length > 0) return exact[0];

  // The disambiguated `<slug>-<id>` form.
  const suffix = /-(\d+)$/.exec(param);
  if (suffix) {
    const id = Number(suffix[1]);
    const found = candidates.find((p) => p.id === id);
    if (found) return found;
  }

  return undefined;
}

/**
 * The routes a co-brand registers on. Structural on purpose: @alight/enrolment-ui's
 * full `Brand` (colours, logo, address) satisfies it, and so does the bare pair
 * the Worker holds — neither package has to know about the other.
 */
export interface RegistrationBrandPaths {
  /** Where a family registers for this brand's programs. */
  enrolPath: string;
  /** This brand's leader/volunteer sign-up route, when it has one. */
  leaderPath?: string;
}

/**
 * THE LINK A FAMILY FOLLOWS TO REGISTER FOR ONE PROGRAM — the thing the host
 * copies into a text message, a bulletin or a Facebook post.
 *
 * It is not always `/enrol?program=…`, and getting it wrong is silent: a link
 * to the wrong page shows the parent a chooser (or an "unavailable" message)
 * for a program that is open, and they give up. Three things decide it, and
 * all three are the host's own settings on the program:
 *
 *   1. WHICH PAGE. A co-branded program registers on the partner's own page
 *      (`enrolPath` — the church's page, not the Alight one), and a
 *      leaders/volunteer program on that brand's leader page when it has one.
 *      Everything else uses this site's own registration path, which is
 *      "/enrol" here and "/register" on htbh.org.au.
 *   2. WHICH PROGRAM. The readable `?program=` slug, unique-by-construction —
 *      `programParam` above.
 *   3. Nothing else. No id, no brand key: a link built from an id means a
 *      different program in a different database.
 *
 * Returns a site-relative path; the caller prepends its own origin, so this
 * stays testable and can't hard-code a hostname.
 */
export function registrationPath(
  program: ProgramLinkFields,
  all: readonly ProgramLinkFields[],
  opts: { registerPath: string; brands?: Record<string, RegistrationBrandPaths> },
): string {
  const brand = opts.brands?.[(program.brand_key ?? "").trim()];
  const base =
    program.form_key === "leader" && brand?.leaderPath
      ? brand.leaderPath
      : (brand?.enrolPath ?? opts.registerPath);
  return `${base}?program=${encodeURIComponent(programParam(program, all))}`;
}

/**
 * THE SAME LINK, CARRYING THE HOST'S PRIVATE LATE-REGISTRATION CODE — the one
 * sanctioned way past a program's enrolment cut-off.
 *
 * It exists as a function, rather than a string the admin screen glues
 * together, because the gluing is where it broke: `registrationPath` already
 * ENDS in "?program=…", and appending a second "?program=…&code=…" swallowed
 * the whole thing into the program value. The slug then matched no program, the
 * journey never asked whether the code was good, and the family landed on
 * exactly the "registrations are closed" card the link exists to get past —
 * silently, because a link to the wrong place does not error.
 *
 * So: one "?", the code appended with "&", and the rule pinned by a test.
 */
export function lateRegistrationPath(
  program: ProgramLinkFields,
  all: readonly ProgramLinkFields[],
  opts: { registerPath: string; brands?: Record<string, RegistrationBrandPaths> },
  code: string,
): string {
  return `${registrationPath(program, all, opts)}&code=${encodeURIComponent(code)}`;
}

/**
 * WHERE EACH CO-BRAND REGISTERS, in one place.
 *
 * The site knows this from @alight/enrolment-ui's BRANDS, but the Worker has
 * to resolve the same destination when it answers a share link — and two
 * copies of a path is how a shared link quietly starts landing on a 404. The
 * brand definitions in enrolment-ui spread these values, so there is exactly
 * one place to change a route.
 *
 * Keys match `programs.brand_key`.
 */
export const BRAND_REGISTRATION_PATHS = {
  htbh: { enrolPath: "/holiday-club", leaderPath: "/leaders" },
  // `satisfies`, not a Record annotation: the brand definitions in
  // enrolment-ui spread `BRAND_REGISTRATION_PATHS.htbh` and need it typed as
  // present, while the Worker still passes the whole object as `brands`.
} satisfies Record<string, RegistrationBrandPaths>;

/* ===========================================================================
 * The registration spreadsheet — shared by the admin CSV download and the live
 * Google Sheets feed. Re-exported LAST: enrolment-csv.ts reads constants from
 * this module, so it must evaluate after them.
 * ========================================================================= */
export * from "./enrolment-csv";

/* First/last name splitting — pure string work with no dependency on the
 * schemas above, shared by the spreadsheet columns and the admin sorting. */
export * from "./person-name";

/* Phone numbers written so a spreadsheet cannot eat the leading zero. */
export * from "./phone";

/* Site events ("what's on") — separate from enrolment programs; pure helpers
 * for upcoming/past, recurrence wording, .ics and Sydney wall-clock time. */
export * from "./site-events";
